import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Search, Plus, Wand2, Trash2, Save, X, Upload, Copy,
  ChevronRight, Swords, Package, Shield, Users, Eye, Crown, Download,
  Check, Sparkles, Zap, Flame, Mail, Image as ImageIcon, Handshake, Languages,
  Trophy,
} from "lucide-react";
import GMDamageCalculator from "@/gm/GMDamageCalculator";
import {
  gmStore, ENEMY_TYPES, ITEM_TYPES, RARITIES, ANIMATIONS, STAT_PRESETS, CUSTOM_STAT_KEYS, GM_STATS,
  generateRandomEnemy, generateRandomItem, generateRandomCharacter,
  generateRandomWorldBoss, generateRandomAbility, seedFromGame, pullFromServer,
  animationClassNames, getRarityColor,
} from "@/gm/gmStore";
import ElementsSection from "@/gm/ElementsSection";
import ForgeSection from "@/gm/ForgeSection";
import SponsorshipInbox from "@/gm/SponsorshipInbox";
import ElementSelect from "@/gm/ElementSelect";
import PlayersLivePanel from "@/gm/PlayersLivePanel";
import AdminRolesPanel from "@/gm/AdminRolesPanel";
import GraphicsPanel from "@/gm/GraphicsPanel";
import PartnersPanel from "@/gm/PartnersPanel";
import TranslationsOverridePanel from "@/gm/TranslationsOverridePanel";
import SkillLeaderboardPanel from "@/gm/SkillLeaderboardPanel";
import "@/gm/gm-animations.css";

/**
 * /admin/gm — Game Master Tool. GM data is cached in localStorage, synchronized
 * to MongoDB, and class compiler changes are also persisted to game_data.py.
 */
export default function AdminGM() {
  const navigate = useNavigate();
  const [gate, setGate] = useState("checking");
  const [tab, setTab] = useState("enemies");
  const [translating, setTranslating] = useState(false);
  const [translatingQ, setTranslatingQ] = useState(false);
  const [langStats, setLangStats] = useState(null);
  const [langHistory, setLangHistory] = useState([]);
  useEffect(() => {
    // Fire-and-forget: admin only, silent-fail if not authorized.
    api.gmLanguageStats().then(setLangStats).catch(() => {});
    api.gmLanguageStatsHistory().then(r => setLangHistory(r.history || [])).catch(() => {});
  }, []);

  useEffect(() => {
    api.moderationMe().then(async m => {
      if (m?.is_admin) {
        // Pull latest GM state from server so the tool syncs across browsers
        await pullFromServer();
        setGate("ok");
      } else {
        setGate("denied");
      }
    }).catch(() => setGate("denied"));
  }, []);

  if (gate === "checking") return <FullScreen>SUMMONING GM CONSOLE...</FullScreen>;
  if (gate === "denied") return (
    <FullScreen>
      <div className="text-[#E33333] mb-3">Accesso negato</div>
      <button data-testid="gm-back-denied" onClick={() => navigate("/game")}
        className="px-4 py-2 border border-[#B89C3A] text-[#E5C34A] font-heading text-xs tracking-widest uppercase hover:bg-[#B89C3A]/10">
        Torna al gioco
      </button>
    </FullScreen>
  );

  const tabGroups = [
    {
      key: "content",
      label: "Contenuti",
      tabs: [
        { id: "enemies",      label: "Nemici",       icon: Swords  },
        { id: "world_bosses", label: "World Boss",   icon: Crown   },
        { id: "items",        label: "Oggetti",      icon: Package },
        { id: "abilities",    label: "Abilità",      icon: Sparkles },
        { id: "elements",     label: "Elementi",     icon: Flame   },
        { id: "classes",      label: "Classi",       icon: Shield  },
      ],
    },
    {
      key: "systems",
      label: "Sistemi",
      tabs: [
        { id: "forge",        label: "Forge",        icon: Sparkles },
        { id: "damage_calc",  label: "Damage Calc",  icon: Zap     },
        { id: "graphics",     label: "Grafiche",     icon: ImageIcon },
      ],
    },
    {
      key: "players",
      label: "Giocatori",
      tabs: [
        { id: "players_live",       label: "Live Players",   icon: Users   },
        { id: "characters",         label: "Personaggi",     icon: Users   },
        { id: "admin_roles",        label: "Admin",          icon: Shield  },
        { id: "skill_leaderboard",  label: "Leaderboard",    icon: Trophy  },
      ],
    },
    {
      key: "outreach",
      label: "Comunicazione",
      tabs: [
        { id: "sponsorship",  label: "Sponsor",      icon: Mail    },
        { id: "partners",     label: "Partner",      icon: Handshake },
      ],
    },
    {
      key: "i18n",
      label: "Localizzazione",
      tabs: [
        { id: "translations_override", label: "Traduzioni", icon: Languages },
      ],
    },
  ];
  const tabs = tabGroups.flatMap(g => g.tabs);

  const doSeed = () => {
    const r = seedFromGame();
    toast.success(`Importati dal gioco: ${r.enemies} nemici, ${r.items} oggetti, ${r.classes} classi, ${r.world_bosses} world boss`);
    // Force refresh by remounting current tab
    setTab(t => t);
    window.location.reload();
  };

  const doTranslateAll = async () => {
    if (translating) return;
    if (!window.confirm("Traduci tutti i contenuti GM (nemici/oggetti/boss/abilità) in EN/DE/FIL/RU con Gemini 3 Flash?\nPuò richiedere alcuni minuti.")) return;
    setTranslating(true);
    try {
      const r = await api.gmTranslateAll(false);
      const rs = r.results || {};
      const parts = Object.entries(rs).map(([k, v]) => `${k}: ${v.translated || 0} nuovi (${v.skipped || 0} skip, ${v.failed || 0} err)`);
      toast.success("Traduzione completata\n" + parts.join("\n"), { duration: 8000 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore durante la traduzione");
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-[#E2DFD8]" data-testid="admin-gm-page">
      <div className="border-b border-[#262626] px-4 sm:px-6 py-3 flex items-center gap-3 sticky top-0 bg-[#050505] z-20">
        <button data-testid="gm-back" onClick={() => navigate("/game")}
          className="text-[#666] hover:text-[#E2DFD8]"><ArrowLeft className="w-4 h-4" /></button>
        <span className="font-heading text-sm tracking-[0.3em] uppercase text-[#E5C34A]">Game Master · Console</span>
        <span className="hidden sm:inline font-mono text-[9px] tracking-widest uppercase text-[#666] ml-2">
          localStorage · nessun impatto sul gioco live
        </span>
        <button data-testid="gm-seed" onClick={doSeed}
          className="ml-auto px-3 py-1.5 border border-[#5D2F5F] text-[#A97AB0] hover:bg-[#5D2F5F]/10 font-heading text-[10px] tracking-widest uppercase flex items-center gap-1.5">
          <Download className="w-3 h-3" /> Importa dati gioco
        </button>
        <button data-testid="gm-translate-all" onClick={doTranslateAll} disabled={translating}
          className="px-3 py-1.5 border border-[#4A7BC7] text-[#6FA8DC] hover:bg-[#4A7BC7]/10 disabled:opacity-40 font-heading text-[10px] tracking-widest uppercase flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> {translating ? "Traduzione..." : "Traduci tutto"}
        </button>
        <button data-testid="gm-translate-quests" disabled={translatingQ}
          onClick={async () => {
            if (translatingQ) return;
            if (!window.confirm("Tradurre tutte le quest templates in EN/DE/FIL/RU via Gemini 3 Flash?")) return;
            setTranslatingQ(true);
            try {
              const r = await api.gmTranslateQuests(false);
              const s = r.stats || {};
              toast.success(`Quest tradotte: ${s.translated || 0} (skip ${s.skipped || 0}, err ${s.failed || 0})`, { duration: 6000 });
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Errore traduzione quest");
            } finally { setTranslatingQ(false); }
          }}
          className="px-3 py-1.5 border border-[#4A7BC7] text-[#6FA8DC] hover:bg-[#4A7BC7]/10 disabled:opacity-40 font-heading text-[10px] tracking-widest uppercase flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> {translatingQ ? "Quest..." : "Traduci quest"}
        </button>
        {langStats && langStats.stats?.length > 0 && (
          <div data-testid="gm-language-stats" title="Distribuzione lingue player + trend"
            className="flex items-center gap-2 px-2 py-1.5 border border-[#333] text-[#B0ADA5] font-mono text-[9px] tracking-widest uppercase">
            <a href={`${(process.env.REACT_APP_BACKEND_URL||"")}/api/admin/gm/language-stats/csv`}
              target="_blank" rel="noopener noreferrer"
              data-testid="gm-language-stats-csv"
              className="text-[#6FA8DC] hover:text-[#8fb8dc] underline decoration-dotted"
              title="Scarica CSV completo">CSV</a>
            {langStats.stats.map(s => {
              const flag = { it: "🇮🇹", en: "🇬🇧", de: "🇩🇪", fil: "🇵🇭", ru: "🇷🇺" }[s.lang] || "🌐";
              // Sparkline: pull the pct for this lang across the last N days.
              const points = langHistory.map(d => (d.stats?.find(x => x.lang === s.lang)?.pct || 0));
              const max = Math.max(1, ...points);
              const w = 32, h = 10;
              const path = points.length > 1 ? points.map((p, i) => {
                const x = (i / (points.length - 1)) * w;
                const y = h - (p / max) * h;
                return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
              }).join(" ") : "";
              return <span key={s.lang} className="flex items-center gap-1" title={`${s.lang.toUpperCase()} · ${s.count} player`}>
                <span>{flag}</span><span className="text-[#E5C34A]">{s.pct}%</span>
                {points.length > 1 && (
                  <svg width={w} height={h} className="opacity-70">
                    <path d={path} fill="none" stroke="#E5C34A" strokeWidth="1" />
                  </svg>
                )}
              </span>;
            })}
          </div>
        )}
      </div>

      <div className="max-w-7xl mx-auto p-3 sm:p-6">
        {/* Grouped tabs (Contenuti / Sistemi / Giocatori / …) */}
        <div className="mb-4 space-y-1 border-b border-[#262626] pb-2" data-testid="gm-tabs">
          {tabGroups.map(group => (
            <div key={group.key} className="flex items-center gap-2 flex-wrap" data-testid={`gm-group-${group.key}`}>
              <span className="font-mono text-[9px] tracking-widest uppercase text-[#666] w-24 shrink-0">
                {group.label}
              </span>
              <div className="flex overflow-x-auto gap-1">
                {group.tabs.map(({ id, label, icon: Icon }) => (
                  <button key={id} data-testid={`gm-tab-${id}`} onClick={() => setTab(id)}
                    className={`shrink-0 flex items-center gap-2 px-3 py-1.5 font-heading text-[10px] tracking-widest uppercase border transition-colors
                      ${tab === id ? "border-[#E5C34A] text-[#E5C34A] bg-[#B89C3A]/10" : "border-transparent text-[#666] hover:text-[#B0ADA5]"}`}>
                    <Icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {tab === "enemies"      && <EnemiesSection />}
        {tab === "world_bosses" && <WorldBossesSection />}
        {tab === "items"        && <ItemsSection />}
        {tab === "classes"      && <ClassesSection />}
        {tab === "elements"     && <ElementsSection />}
        {tab === "forge"        && <ForgeSection />}
        {tab === "characters"   && <CharactersSection />}
        {tab === "players_live" && <PlayersLivePanel />}
        {tab === "admin_roles"  && <AdminRolesPanel />}
        {tab === "abilities"    && <AbilitiesSection />}
        {tab === "sponsorship"  && <SponsorshipInbox />}
        {tab === "damage_calc"  && <GMDamageCalculator />}
        {tab === "graphics"     && <GraphicsPanel />}
        {tab === "partners"     && <PartnersPanel />}
        {tab === "translations_override" && <TranslationsOverridePanel />}
        {tab === "skill_leaderboard" && <SkillLeaderboardPanel />}
      </div>
    </div>
  );
}

// ── SHARED COMPONENTS ─────────────────────────────────────────────

function FullScreen({ children }) {
  return <div className="min-h-screen flex flex-col items-center justify-center bg-[#050505] text-[#8c8c8c] font-heading tracking-widest gap-4 px-4">
    {typeof children === "string" ? <span className="ember-pulse">{children}</span> : children}
  </div>;
}

/** Image with configurable animations. Placeholder if no src. */
export function GMImage({ src, alt, animations, className = "", size = 96, testid }) {
  const anims = animationClassNames(animations);
  const style = { width: size, height: size };
  if (!src) return (
    <div data-testid={testid} className={`gm-image-placeholder gm-image-wrap ${anims} ${className}`} style={style}>
      no img
    </div>
  );
  return (
    <div data-testid={testid} className={`gm-image-wrap ${anims} ${className}`} style={style}>
      <img src={src} alt={alt || ""} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
    </div>
  );
}

function Input({ label, value, onChange, type = "text", placeholder, testid, textarea, rows = 3, options }) {
  const cls = "w-full bg-[#0a0a0a] border border-[#333] px-2.5 py-1.5 text-xs text-[#E2DFD8] focus:border-[#B89C3A] outline-none";
  return (
    <label className="block">
      {label && <div className="font-mono text-[9px] tracking-widest uppercase text-[#8c8c8c] mb-1">{label}</div>}
      {textarea ? (
        <textarea data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder} rows={rows} className={`${cls} resize-none`} />
      ) : options ? (
        <select data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={cls}>
          <option value="">—</option>
          {options.map(o => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
        </select>
      ) : (
        <input data-testid={testid} type={type} value={value ?? ""} onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)}
          placeholder={placeholder} className={cls} />
      )}
    </label>
  );
}

function ImageField({ value, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("Max 4MB"); return; }
    setBusy(true);
    try {
      // Prefer server-side storage: file lands under backend/assets/gm_images
      // and survives redeploys. If the upload endpoint is unreachable
      // (offline / not-admin) fall back to the legacy base64 encoding so the
      // GM tool remains usable — the next sync will convert it.
      const r = await api.gmUploadImage(f);
      onChange(r.url);
      toast.success("Immagine caricata");
    } catch (err) {
      const reader = new FileReader();
      reader.onload = () => onChange(reader.result);
      reader.readAsDataURL(f);
      toast.message("Upload non riuscito, salvata inline (verrà convertita al prossimo salvataggio)");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-1">
      <div className="font-mono text-[9px] tracking-widest uppercase text-[#8c8c8c]">Immagine (URL o upload)</div>
      <input data-testid={`${testid}-url`} type="text" value={value || ""} onChange={(e) => onChange(e.target.value)}
        placeholder="https://... oppure lascia vuoto"
        className="w-full bg-[#0a0a0a] border border-[#333] px-2.5 py-1.5 text-xs text-[#E2DFD8] font-mono focus:border-[#B89C3A] outline-none" />
      <label className={`inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest cursor-pointer ${busy ? "text-[#666] pointer-events-none" : "text-[#B89C3A] hover:text-[#E5C34A]"}`}>
        <Upload className="w-3 h-3" /> {busy ? "caricamento..." : "upload file"}
        <input data-testid={`${testid}-file`} type="file" accept="image/*" onChange={onFile} className="hidden" />
      </label>
    </div>
  );
}

function AnimationPicker({ value = [], onChange, testid }) {
  const toggle = (k) => {
    const has = value.includes(k);
    onChange(has ? value.filter(x => x !== k) : [...value, k]);
  };
  return (
    <div>
      <div className="font-mono text-[9px] tracking-widest uppercase text-[#8c8c8c] mb-1">Animazioni immagine</div>
      <div className="flex flex-wrap gap-1" data-testid={testid}>
        {ANIMATIONS.map(a => (
          <button key={a.key} type="button" onClick={() => toggle(a.key)}
            data-testid={`${testid}-${a.key}`}
            className={`px-2 py-1 font-mono text-[9px] tracking-widest uppercase border transition-colors
              ${value.includes(a.key)
                ? (a.kind === "hover" ? "border-[#E55A00] text-[#E55A00] bg-[#E55A00]/10" : "border-[#6E8B3D] text-[#6E8B3D] bg-[#4A5D23]/10")
                : "border-[#262626] text-[#666] hover:text-[#B0ADA5]"}`}>
            {a.label}
          </button>
        ))}
      </div>
      <div className="mt-1 font-mono text-[9px] text-[#666]">Nessuna scelta → Idle Float di default</div>
    </div>
  );
}

function SearchBar({ value, onChange, filters, testid }) {
  return (
    <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
      <div className="relative flex-1">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#666]" strokeWidth={1.5} />
        <input data-testid={`${testid}-search`} value={value} onChange={e => onChange(e.target.value)}
          placeholder="Cerca per nome..." className="w-full bg-[#0a0a0a] border border-[#333] pl-8 pr-3 py-2 text-xs text-[#E2DFD8] outline-none focus:border-[#B89C3A]" />
      </div>
      {filters}
    </div>
  );
}

function Header({ title, count, onNew, onRandom, testid, hideRandom = false, hideNew = false }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
      <div>
        <div className="font-heading text-sm tracking-[0.3em] uppercase text-[#E5C34A]">{title}</div>
        <div className="font-mono text-[9px] tracking-widest uppercase text-[#666]">{count} salvati</div>
      </div>
      <div className="sm:ml-auto flex gap-2">
        {!hideRandom && <button data-testid={`${testid}-random`} onClick={onRandom}
          className="px-3 py-2 border border-[#5D2F5F] text-[#A97AB0] hover:bg-[#5D2F5F]/10 font-heading text-[10px] tracking-widest uppercase flex items-center gap-1.5">
          <Wand2 className="w-3 h-3" /> Genera Casuale
        </button>}
        {!hideNew && <button data-testid={`${testid}-new`} onClick={onNew}
          className="px-3 py-2 border border-[#B89C3A] text-[#E5C34A] hover:bg-[#B89C3A]/10 font-heading text-[10px] tracking-widest uppercase flex items-center gap-1.5">
          <Plus className="w-3 h-3" /> Nuovo
        </button>}
      </div>
    </div>
  );
}

function ConfirmDelete({ onCancel, onConfirm, name }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center px-4" data-testid="gm-confirm-delete">
      <div className="panel p-5 max-w-sm w-full space-y-3">
        <div className="font-heading text-sm tracking-widest uppercase text-[#E33333]">Confermi eliminazione?</div>
        <div className="font-body text-xs text-[#B0ADA5]">Stai per eliminare <b>{name}</b>. L'operazione è definitiva.</div>
        <div className="flex justify-end gap-2 pt-2 border-t border-[#262626]">
          <button data-testid="gm-cancel-delete" onClick={onCancel} className="px-3 py-1.5 border border-[#262626] text-[#8c8c8c] font-heading text-[10px] tracking-widest uppercase">Annulla</button>
          <button data-testid="gm-confirm-delete-btn" onClick={onConfirm} className="px-3 py-1.5 border border-[#E33333] text-[#E33333] font-heading text-[10px] tracking-widest uppercase hover:bg-[#8A0303]/10">Elimina</button>
        </div>
      </div>
    </div>
  );
}

function Modal({ title, onClose, children, wide, testid }) {
  return (
    <div className="fixed inset-0 z-40 bg-black/80 flex items-center justify-center px-3 sm:px-6 py-4 overflow-y-auto" data-testid={testid}>
      <div className={`panel w-full ${wide ? "max-w-4xl" : "max-w-xl"} space-y-3 my-auto`}>
        <div className="flex items-center gap-2 border-b border-[#262626] px-4 py-3 sticky top-0 bg-[#050505]">
          <span className="font-heading text-sm tracking-[0.3em] uppercase text-[#E5C34A]">{title}</span>
          <button data-testid={`${testid}-close`} onClick={onClose} className="ml-auto text-[#666] hover:text-[#E2DFD8]"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-4 pb-4 space-y-3">{children}</div>
      </div>
    </div>
  );
}

// ── ENEMIES SECTION ───────────────────────────────────────────────

function EnemiesSection() {
  const [list, setList] = useState(gmStore.enemies.list());
  const [q, setQ] = useState(""); const [ftype, setFtype] = useState(""); const [flevel, setFlevel] = useState("");
  const [editing, setEditing] = useState(null); const [viewing, setViewing] = useState(null); const [deleting, setDeleting] = useState(null);

  const refresh = () => setList(gmStore.enemies.list());
  const filtered = useMemo(() => list.filter(e =>
    (!q || e.name.toLowerCase().includes(q.toLowerCase())) &&
    (!ftype || e.type === ftype) &&
    (!flevel || String(e.level) === flevel)
  ), [list, q, ftype, flevel]);

  const doRandom = () => { const items = gmStore.items.list(); const e = generateRandomEnemy(items); gmStore.enemies.upsert(e); refresh(); toast.success(`Generato: ${e.name}`); };
  const save = (e) => {
    let toSave = { ...e };
    if (e.stat_preset === "custom") {
      // Trust the raw inputs the admin typed and normalize them to numbers.
      CUSTOM_STAT_KEYS.forEach(k => { toSave[k] = Number(toSave[k] ?? 0) || 0; });
    } else {
      const preset = STAT_PRESETS.find(p => p.key === e.stat_preset) || STAT_PRESETS[1];
      const combatStats = Object.fromEntries(Object.entries(preset).filter(([k]) => !["key", "label", "xp", "gold"].includes(k)));
      toSave = { ...toSave, ...combatStats };
    }
    gmStore.enemies.upsert(toSave); refresh(); setEditing(null); toast.success("Salvato");
  };
  const del = () => { gmStore.enemies.remove(deleting.id); refresh(); setDeleting(null); toast.success("Eliminato"); };

  return (
    <div className="space-y-3">
      <Header title="Nemici" count={list.length} testid="gm-enemies" hideRandom
        onNew={() => setEditing(defaultEnemy())} />
      <SearchBar value={q} onChange={setQ} testid="gm-enemies"
        filters={<div className="flex gap-2">
          <select data-testid="gm-enemies-filter-type" value={ftype} onChange={e => setFtype(e.target.value)}
            className="bg-[#0a0a0a] border border-[#333] px-2 py-2 text-xs text-[#B0ADA5]">
            <option value="">Tutti i tipi</option>
            {ENEMY_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
          <input data-testid="gm-enemies-filter-level" type="number" value={flevel} onChange={e => setFlevel(e.target.value)}
            placeholder="Livello" className="w-24 bg-[#0a0a0a] border border-[#333] px-2 py-2 text-xs text-[#E2DFD8]" />
        </div>} />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {filtered.map(e => (
          <div key={e.id} data-testid={`gm-enemy-card-${e.id}`}
            className="panel p-3 flex gap-3 cursor-pointer hover:border-[#E5C34A] transition-colors" onClick={() => setViewing(e)}>
            <GMImage src={e.image} animations={e.animations} size={72} testid={`gm-enemy-img-${e.id}`} />
            <div className="flex-1 min-w-0">
              <div className="font-heading text-[13px] tracking-widest uppercase text-[#E5C34A] truncate">{e.name}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#B89C3A]">Lv {e.level} · {e.type}</div>
              <div className="font-mono text-[10px] text-[#B0ADA5] mt-1">HP {e.hp} · DEF {e.def} · {e.rank}</div>
              {typeof e.spawn_rate === "number" && (
                <div className="font-mono text-[9px] text-[#B89C3A]">Spawn: {e.spawn_rate}%</div>
              )}
              <div className="flex gap-2 mt-2">
                <button onClick={(ev) => { ev.stopPropagation(); setEditing(e); }} data-testid={`gm-enemy-edit-${e.id}`}
                  className="font-mono text-[9px] uppercase tracking-widest text-[#B89C3A] hover:text-[#E5C34A]">Modifica</button>
                <button onClick={(ev) => { ev.stopPropagation(); setDeleting(e); }} data-testid={`gm-enemy-del-${e.id}`}
                  className="font-mono text-[9px] uppercase tracking-widest text-[#E33333] hover:text-[#8A0303] ml-auto">Elimina</button>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="col-span-full text-center text-xs italic text-[#666] py-8">Nessun nemico. Usa "Genera Casuale" o "Nuovo".</div>}
      </div>

      {editing && <EnemyEditor initial={editing} onCancel={() => setEditing(null)} onSave={save} />}
      {viewing && <EnemyView e={viewing} onClose={() => setViewing(null)} onEdit={() => { setEditing(viewing); setViewing(null); }} />}
      {deleting && <ConfirmDelete name={deleting.name} onCancel={() => setDeleting(null)} onConfirm={del} />}
    </div>
  );
}

function defaultEnemy() { return { id: crypto.randomUUID(), name: "", level: 1, rank: "Trascurabile", type: "Umanoide", stat_preset: "standard", resistances: [], weaknesses: [], abilities: [], ability_ids: [], lore: "", drops: [], spawn_rate: 20, xp: 80, gold: 10, image: "", notes: "", animations: ["idle-float"] }; }

function EnemyEditor({ initial, onCancel, onSave }) {
  const [e, set] = useState(initial);
  const u = (k, v) => set({ ...e, [k]: v });
  const items = gmStore.items.list();
  return (
    <Modal title={initial.name ? "Modifica Nemico" : "Nuovo Nemico"} onClose={onCancel} wide testid="gm-enemy-editor">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input label="Nome" value={e.name} onChange={v => u("name", v)} testid="enemy-name" />
        <Input label="Tipo" value={e.type} onChange={v => u("type", v)} options={ENEMY_TYPES} testid="enemy-type" />
        <Input label="Livello" type="number" value={e.level} onChange={v => u("level", v)} testid="enemy-level" />
        <Input label="Rank" value={e.rank} onChange={v => u("rank", v)}
          options={["Trascurabile", "Sfida", "Elite", "Boss", "Leggenda"]} testid="enemy-rank" />
      </div>
      <StatPresetPicker value={e.stat_preset} onChange={v => u("stat_preset", v)} presets={STAT_PRESETS} testid="enemy-stat-preset" entity={e} onEntityChange={set} />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <Input label="Spawn %" type="number" value={e.spawn_rate ?? 20} onChange={v => u("spawn_rate", v)} testid="enemy-spawn-rate" />
        <Input label="XP ricompensa" type="number" value={e.xp ?? 80} onChange={v => u("xp", v)} testid="enemy-xp" />
        <Input label="Oro ricompensa" type="number" value={e.gold ?? 10} onChange={v => u("gold", v)} testid="enemy-gold" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <ElementListEditor label="Resistenze elementali" value={e.resistances} onChange={v => u("resistances", v)} testid="enemy-resistances" />
        <ElementListEditor label="Debolezze elementali" value={e.weaknesses} onChange={v => u("weaknesses", v)} testid="enemy-weaknesses" />
      </div>
      <ElementSelect label="Elemento di attacco" value={e.attack_element || ""} onChange={v => u("attack_element", v)} testid="enemy-attack-element" />
      <AbilityAssignmentField value={e.ability_ids} onChange={v => u("ability_ids", v)} target="enemy" testid="enemy-abilities" />
      <Input label="Descrizione / Lore" textarea rows={3} value={e.lore} onChange={v => u("lore", v)} testid="enemy-lore" />
      <DropsField drops={e.drops} onChange={v => u("drops", v)} items={items} />
      <ImageField value={e.image} onChange={v => u("image", v)} testid="enemy-image" />
      <AnimationPicker value={e.animations} onChange={v => u("animations", v)} testid="enemy-anims" />
      <Input label="Note" textarea rows={2} value={e.notes} onChange={v => u("notes", v)} testid="enemy-notes" />
      <div className="flex justify-end gap-2 pt-2 border-t border-[#262626]">
        <button onClick={onCancel} className="px-3 py-2 border border-[#262626] font-heading text-[10px] tracking-widest uppercase text-[#8c8c8c]">Annulla</button>
        <button data-testid="enemy-save" onClick={() => onSave(e)}
          className="px-3 py-2 border border-[#6E8B3D] text-[#6E8B3D] font-heading text-[10px] tracking-widest uppercase hover:bg-[#4A5D23]/10 flex items-center gap-1.5">
          <Save className="w-3 h-3" /> Salva
        </button>
      </div>
    </Modal>
  );
}

function CSVField({ label, value, onChange, testid }) {
  const [text, setText] = useState((value || []).join(", "));
  useEffect(() => { setText((value || []).join(", ")); }, [value]);
  return (
    <Input label={label} value={text} testid={testid}
      onChange={v => { setText(v); onChange(v.split(",").map(x => x.trim()).filter(Boolean)); }}
      placeholder="valore1, valore2, ..." />
  );
}

// §6C: Structured item effects. Admin adds one effect per row, choosing a
// KIND (stat/resistance/max_hp/max_mana/max_vigor/element_damage/remove_dot/…)
// and its parameters. The game engine reads gm_effects and applies them.
const EFFECT_KINDS = [
  { key: "stat",           label: "Aumenta statistica",   fields: ["stat", "value"] },
  { key: "resistance",     label: "Resistenza elementale (%)", fields: ["element", "value"] },
  { key: "weakness",       label: "Debolezza elementale (%)",  fields: ["element", "value"] },
  { key: "max_hp",         label: "+HP MAX",              fields: ["value"] },
  { key: "max_mana",       label: "+Mana MAX",            fields: ["value"] },
  { key: "max_vigor",      label: "+Vigor MAX",           fields: ["value"] },
  { key: "max_conc",       label: "+Concentrazione MAX",  fields: ["value"] },
  { key: "element_damage", label: "Danno elementale (armi)", fields: ["element", "value"] },
  { key: "remove_dot",     label: "Rimuove DoT",          fields: [] },
  { key: "remove_status",  label: "Rimuove status",       fields: ["status"] },
];
const STAT_KEYS = ["strength", "defense", "dexterity", "intelligence", "charisma", "concentration"];

function ItemEffectsEditor({ value, onChange }) {
  const rows = Array.isArray(value) ? value : [];
  const update = (i, patch) => {
    const next = [...rows]; next[i] = { ...next[i], ...patch }; onChange(next);
  };
  const remove = (i) => onChange(rows.filter((_, j) => j !== i));
  const add = () => onChange([...rows, { kind: "stat", stat: "strength", value: 1 }]);
  return (
    <div className="panel-inner p-2 border border-[#333]" data-testid="item-effects-editor">
      <div className="font-mono text-[9px] tracking-widest uppercase text-[#E5C34A] mb-1">Effetti strutturati</div>
      <div className="space-y-1">
        {rows.map((eff, i) => {
          const kind = EFFECT_KINDS.find(k => k.key === eff.kind) || EFFECT_KINDS[0];
          return (
            <div key={i} className="flex flex-wrap gap-1 items-end" data-testid={`item-effect-${i}`}>
              <select value={eff.kind} onChange={e => update(i, { kind: e.target.value })}
                className="bg-[#0a0a0a] border border-[#333] px-2 py-1.5 text-xs text-[#E2DFD8]" data-testid={`item-effect-${i}-kind`}>
                {EFFECT_KINDS.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
              </select>
              {kind.fields.includes("stat") && (
                <select value={eff.stat || "strength"} onChange={e => update(i, { stat: e.target.value })}
                  className="bg-[#0a0a0a] border border-[#333] px-2 py-1.5 text-xs text-[#E2DFD8]" data-testid={`item-effect-${i}-stat`}>
                  {STAT_KEYS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              )}
              {kind.fields.includes("element") && (
                <div className="min-w-[7rem]"><ElementSelect value={eff.element || ""} onChange={v => update(i, { element: v })} testid={`item-effect-${i}-element`} /></div>
              )}
              {kind.fields.includes("status") && (
                <input value={eff.status || ""} onChange={e => update(i, { status: e.target.value })}
                  className="bg-[#0a0a0a] border border-[#333] px-2 py-1.5 text-xs text-[#E2DFD8] w-32" placeholder="status name" data-testid={`item-effect-${i}-status`} />
              )}
              {kind.fields.includes("value") && (
                <input type="number" value={eff.value || 0} onChange={e => update(i, { value: Number(e.target.value) })}
                  className="w-20 bg-[#0a0a0a] border border-[#333] px-2 py-1.5 text-xs text-[#E2DFD8]" placeholder="value" data-testid={`item-effect-${i}-value`} />
              )}
              <button type="button" onClick={() => remove(i)} className="px-2 border border-[#262626] text-[#E33333] hover:bg-[#8A0303]/10">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          );
        })}
        <button type="button" onClick={add} data-testid="item-effect-add"
          className="font-mono text-[10px] uppercase tracking-widest text-[#B89C3A] hover:text-[#E5C34A] flex items-center gap-1">
          <Plus className="w-3 h-3" /> aggiungi effetto
        </button>
      </div>
    </div>
  );
}

// §6C: multi-row dismantle materials — {itemId, quantity} pointing to real
// gm_items. Server-side resolver looks up the live item at dismantle time so
// admin edits propagate.
function DismantleMaterialsField({ value, onChange, items }) {
  const rows = Array.isArray(value) ? value : [];
  const update = (i, patch) => { const next = [...rows]; next[i] = { ...next[i], ...patch }; onChange(next); };
  const remove = (i) => onChange(rows.filter((_, j) => j !== i));
  const add = () => onChange([...rows, { itemId: "", quantity: 1 }]);
  return (
    <div className="panel-inner p-2 border border-[#333]" data-testid="item-dismantle-editor">
      <div className="font-mono text-[9px] tracking-widest uppercase text-[#B89C3A] mb-1">Materiali Dismantle (multi-riga)</div>
      <div className="space-y-1">
        {rows.map((m, i) => (
          <div key={i} className="flex gap-2 items-end" data-testid={`item-dismantle-${i}`}>
            <select value={m.itemId || ""} onChange={e => update(i, { itemId: e.target.value })}
              className="flex-1 bg-[#0a0a0a] border border-[#333] px-2 py-1.5 text-xs text-[#E2DFD8]"
              data-testid={`item-dismantle-${i}-item`}>
              <option value="">— seleziona materiale —</option>
              {items.map(it => <option key={it.id} value={it.id}>{it.name} ({it.rarity})</option>)}
            </select>
            <input type="number" min="1" value={m.quantity || 1} onChange={e => update(i, { quantity: Math.max(1, Number(e.target.value)) })}
              className="w-20 bg-[#0a0a0a] border border-[#333] px-2 py-1.5 text-xs text-[#E2DFD8]"
              placeholder="qty" data-testid={`item-dismantle-${i}-qty`} />
            <button type="button" onClick={() => remove(i)} className="px-2 border border-[#262626] text-[#E33333] hover:bg-[#8A0303]/10">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
        <button type="button" onClick={add} data-testid="item-dismantle-add"
          className="font-mono text-[10px] uppercase tracking-widest text-[#B89C3A] hover:text-[#E5C34A] flex items-center gap-1">
          <Plus className="w-3 h-3" /> aggiungi materiale
        </button>
      </div>
    </div>
  );
}


function DropsField({ drops, onChange, items }) {
  return (
    <div>
      <div className="font-mono text-[9px] tracking-widest uppercase text-[#8c8c8c] mb-1">Drop (itemId + drop_rate %)</div>
      <div className="space-y-1">
        {(drops || []).map((d, i) => (
          <div key={i} className="flex gap-2" data-testid={`enemy-drop-${i}`}>
            <select value={d.itemId} onChange={e => { const arr = [...drops]; arr[i] = { ...d, itemId: e.target.value }; onChange(arr); }}
              className="flex-1 bg-[#0a0a0a] border border-[#333] px-2 py-1.5 text-xs text-[#E2DFD8]">
              <option value="">— seleziona oggetto —</option>
              {items.map(it => <option key={it.id} value={it.id}>{it.name} ({it.rarity})</option>)}
            </select>
            <input type="number" value={d.drop_rate ?? d.chance ?? 20}
              onChange={e => { const arr = [...drops]; arr[i] = { ...d, drop_rate: Number(e.target.value), chance: Number(e.target.value) }; onChange(arr); }}
              className="w-20 bg-[#0a0a0a] border border-[#333] px-2 py-1.5 text-xs text-[#E2DFD8]" placeholder="%" />
            <button type="button" onClick={() => onChange(drops.filter((_, j) => j !== i))} className="px-2 border border-[#262626] text-[#E33333] hover:bg-[#8A0303]/10">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
        <button type="button" onClick={() => onChange([...(drops || []), { itemId: "", drop_rate: 20 }])}
          data-testid="enemy-add-drop"
          className="font-mono text-[10px] uppercase tracking-widest text-[#B89C3A] hover:text-[#E5C34A] flex items-center gap-1">
          <Plus className="w-3 h-3" /> aggiungi drop
        </button>
      </div>
    </div>
  );
}

// §6A: Multi-row resistances/weaknesses that use the centralized ElementSelect
// so admins can pick from live gm_elements (§3). Each row is {element, value%}.
function ElementListEditor({ label, value, onChange, testid }) {
  const rows = (value || []).map(r => (typeof r === "string" ? { element: r, value: 100 } : r));
  const update = (i, patch) => {
    const next = [...rows]; next[i] = { ...next[i], ...patch }; onChange(next);
  };
  const remove = (i) => onChange(rows.filter((_, j) => j !== i));
  const add = () => onChange([...rows, { element: "", value: 25 }]);
  return (
    <div data-testid={testid}>
      <div className="font-mono text-[9px] tracking-widest uppercase text-[#8c8c8c] mb-1">{label}</div>
      <div className="space-y-1">
        {rows.map((r, i) => (
          <div key={i} className="flex gap-2 items-end" data-testid={`${testid}-row-${i}`}>
            <div className="flex-1">
              <ElementSelect value={r.element} onChange={v => update(i, { element: v })} testid={`${testid}-${i}-el`} />
            </div>
            <input type="number" value={r.value} onChange={e => update(i, { value: Number(e.target.value) })}
              className="w-20 bg-[#0a0a0a] border border-[#333] px-2 py-1.5 text-xs text-[#E2DFD8]"
              placeholder="%" data-testid={`${testid}-${i}-value`} />
            <button type="button" onClick={() => remove(i)} className="px-2 border border-[#262626] text-[#E33333] hover:bg-[#8A0303]/10">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
        <button type="button" onClick={add} data-testid={`${testid}-add`}
          className="font-mono text-[10px] uppercase tracking-widest text-[#B89C3A] hover:text-[#E5C34A] flex items-center gap-1">
          <Plus className="w-3 h-3" /> aggiungi elemento
        </button>
      </div>
    </div>
  );
}

function EnemyView({ e, onClose, onEdit }) {
  const items = gmStore.items.list();
  return (
    <Modal title={e.name || "Nemico"} onClose={onClose} wide testid="gm-enemy-view">
      <div className="flex flex-col md:flex-row gap-4">
        <GMImage src={e.image} animations={e.animations} size={200} testid="enemy-view-img" />
        <div className="flex-1 space-y-2">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
            <Stat label="Tipo" value={e.type} />
            <Stat label="Livello" value={e.level} />
            <Stat label="Rank" value={e.rank} />
            <Stat label="HP" value={e.hp} />
            <Stat label="Mana" value={e.mana} />
            <Stat label="Difesa" value={e.def} />
            <Stat label="Attacco" value={e.attack} />
            <Stat label="Velocità" value={e.speed} />
            <Stat label="XP" value={e.xp} />
            <Stat label="Oro" value={e.gold} />
            <Stat label="Spawn %" value={typeof e.spawn_rate === "number" ? `${e.spawn_rate}%` : "—"} />
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-1 text-xs">
            {["str","def","dex","int","con","cha"].map(k => <Stat key={k} label={k.toUpperCase()} value={e[k]} />)}
          </div>
        </div>
      </div>
      {e.lore && <Section title="Lore">{e.lore}</Section>}
      {e.abilities?.length > 0 && <Section title="Abilità speciali"><Chips items={e.abilities} /></Section>}
      {e.resistances?.length > 0 && <Section title="Resistenze"><Chips items={e.resistances} color="#6E8B3D" /></Section>}
      {e.weaknesses?.length > 0 && <Section title="Debolezze"><Chips items={e.weaknesses} color="#E33333" /></Section>}
      {e.drops?.length > 0 && <Section title="Drop">
        <div className="space-y-1">
          {e.drops.map((d, i) => {
            const it = items.find(x => x.id === d.itemId);
            return <div key={i} className="font-mono text-xs text-[#B0ADA5] flex gap-2">
              <span className="text-[#E5C34A]" style={it ? { color: getRarityColor(it.rarity) } : {}}>{it?.name || "(?)"}</span>
              <span className="text-[#666]">{d.chance}%</span>
            </div>;
          })}
        </div>
      </Section>}
      {e.notes && <Section title="Note">{e.notes}</Section>}
      <div className="flex justify-end gap-2 pt-2 border-t border-[#262626]">
        <button onClick={onEdit} className="px-3 py-2 border border-[#B89C3A] text-[#E5C34A] font-heading text-[10px] tracking-widest uppercase">Modifica</button>
      </div>
    </Modal>
  );
}

function Stat({ label, value }) {
  return <div className="border border-[#262626] px-2 py-1">
    <div className="font-mono text-[9px] tracking-widest uppercase text-[#666]">{label}</div>
    <div className="text-xs text-[#E2DFD8]">{value ?? "—"}</div>
  </div>;
}
function Section({ title, children }) {
  return <div className="border-t border-[#262626] pt-2">
    <div className="font-heading text-[10px] tracking-widest uppercase text-[#8c8c8c] mb-1">{title}</div>
    <div className="text-xs text-[#B0ADA5] whitespace-pre-wrap">{children}</div>
  </div>;
}
function Chips({ items, color = "#B89C3A" }) {
  return <div className="flex flex-wrap gap-1">
    {items.map((x, i) => <span key={i} className="px-2 py-0.5 border font-mono text-[10px] uppercase tracking-widest" style={{ borderColor: color, color }}>{x}</span>)}
  </div>;
}

// ── ITEMS SECTION ─────────────────────────────────────────────────

function ItemsSection() {
  const [list, setList] = useState(gmStore.items.list());
  const [q, setQ] = useState(""); const [ftype, setFtype] = useState(""); const [frarity, setFrarity] = useState("");
  const [editing, setEditing] = useState(null); const [viewing, setViewing] = useState(null); const [deleting, setDeleting] = useState(null);
  const refresh = () => setList(gmStore.items.list());
  const filtered = list.filter(i => (!q || i.name.toLowerCase().includes(q.toLowerCase())) && (!ftype || i.type === ftype) && (!frarity || i.rarity === frarity));
  const doRandom = () => { const it = generateRandomItem(); gmStore.items.upsert(it); refresh(); toast.success(`Generato: ${it.name}`); };
  const save = (it) => { gmStore.items.upsert(it); refresh(); setEditing(null); toast.success("Salvato"); };
  const del = () => { gmStore.items.remove(deleting.id); refresh(); setDeleting(null); toast.success("Eliminato"); };

  return (
    <div className="space-y-3">
      <Header title="Oggetti" count={list.length} testid="gm-items" hideRandom onNew={() => setEditing(defaultItem())} />
      <SearchBar value={q} onChange={setQ} testid="gm-items"
        filters={<div className="flex gap-2">
          <select data-testid="gm-items-filter-type" value={ftype} onChange={e => setFtype(e.target.value)}
            className="bg-[#0a0a0a] border border-[#333] px-2 py-2 text-xs text-[#B0ADA5]">
            <option value="">Tutti i tipi</option>{ITEM_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
          <select data-testid="gm-items-filter-rarity" value={frarity} onChange={e => setFrarity(e.target.value)}
            className="bg-[#0a0a0a] border border-[#333] px-2 py-2 text-xs text-[#B0ADA5]">
            <option value="">Tutte rarità</option>{RARITIES.map(r => <option key={r.key}>{r.key}</option>)}
          </select>
        </div>} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {filtered.map(it => (
          <div key={it.id} data-testid={`gm-item-card-${it.id}`} onClick={() => setViewing(it)}
            className="panel p-3 flex gap-3 cursor-pointer hover:border-[#E5C34A] transition-colors">
            <GMImage src={it.image} animations={it.animations} size={72} testid={`gm-item-img-${it.id}`} />
            <div className="flex-1 min-w-0">
              <div className="font-heading text-[13px] uppercase tracking-widest truncate" style={{ color: getRarityColor(it.rarity) }}>{it.name}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#B89C3A]">{it.type} · {it.rarity}</div>
              <div className="font-mono text-[10px] text-[#B0ADA5] mt-1">{it.gold_value}g · {it.weight}kg</div>
              <div className="flex gap-2 mt-2">
                <button onClick={(ev) => { ev.stopPropagation(); setEditing(it); }} className="font-mono text-[9px] uppercase tracking-widest text-[#B89C3A] hover:text-[#E5C34A]">Modifica</button>
                <button onClick={(ev) => { ev.stopPropagation(); setDeleting(it); }} className="font-mono text-[9px] uppercase tracking-widest text-[#E33333] hover:text-[#8A0303] ml-auto">Elimina</button>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="col-span-full text-center text-xs italic text-[#666] py-8">Nessun oggetto.</div>}
      </div>
      {editing && <ItemEditor initial={editing} onCancel={() => setEditing(null)} onSave={save} />}
      {viewing && <ItemView it={viewing} onClose={() => setViewing(null)} onEdit={() => { setEditing(viewing); setViewing(null); }} />}
      {deleting && <ConfirmDelete name={deleting.name} onCancel={() => setDeleting(null)} onConfirm={del} />}
    </div>
  );
}

function defaultItem() { return { id: crypto.randomUUID(), name: "", type: "Materiale", rarity: "Comune", description: "", effects: [], gm_effects: [], dismantle_materials: [], element: "", element_damage: 0, resistance_element: "", resistance_value: 0, gold_value: 1, weight: 0.1, requirements: "—", stackable: true, max_stack: 99, image: "", notes: "", animations: ["idle-breathe"], custom_stats: false, power: 1, damage_cap: 0, duration: 0 }; }

function ItemEditor({ initial, onCancel, onSave }) {
  const [it, set] = useState(initial);
  const u = (k, v) => set({ ...it, [k]: v });
  const allItems = gmStore.items.list().filter(x => x.id !== it.id);
  return (
    <Modal title={initial.name ? "Modifica Oggetto" : "Nuovo Oggetto"} onClose={onCancel} wide testid="gm-item-editor">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input label="Nome" value={it.name} onChange={v => u("name", v)} testid="item-name" />
        <Input label="Tipo" options={ITEM_TYPES} value={it.type} onChange={v => u("type", v)} testid="item-type" />
        <Input label="Rarità" options={RARITIES.map(r => r.key)} value={it.rarity} onChange={v => u("rarity", v)} testid="item-rarity" />
        <Input label="Requisiti (testo)" value={it.requirements} onChange={v => u("requirements", v)} testid="item-req" />
      </div>
      <Input label="Descrizione" textarea rows={2} value={it.description} onChange={v => u("description", v)} testid="item-desc" />
      {/* §6C: Structured effects — replace flat CSV with proper editor. Legacy
          `effects` (list of strings) is kept as read-only fallback so old items
          without gm_effects still render their bonus lines. */}
      <ItemEffectsEditor value={it.gm_effects || []} onChange={v => u("gm_effects", v)} />
      {/* Elemental fields — displayed on weapons/armor/trinkets */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="panel-inner p-2 border border-[#333]">
          <div className="font-mono text-[9px] tracking-widest uppercase text-[#E55A00] mb-1">Danno elementale (armi)</div>
          <div className="grid grid-cols-2 gap-2">
            <ElementSelect value={it.element || ""} onChange={v => u("element", v)} testid="item-element" />
            <Input label="Danno" type="number" value={it.element_damage || 0} onChange={v => u("element_damage", Number(v))} testid="item-element-damage" />
          </div>
        </div>
        <div className="panel-inner p-2 border border-[#333]">
          <div className="font-mono text-[9px] tracking-widest uppercase text-[#8FD1E5] mb-1">Resistenza elementale (armature/trinket)</div>
          <div className="grid grid-cols-2 gap-2">
            <ElementSelect value={it.resistance_element || ""} onChange={v => u("resistance_element", v)} testid="item-resistance-element" />
            <Input label="Resistenza %" type="number" value={it.resistance_value || 0} onChange={v => u("resistance_value", Number(v))} testid="item-resistance-value" />
          </div>
        </div>
      </div>
      {/* §6C: Dismantle materials — multi-row picker of real gm_items */}
      <DismantleMaterialsField value={it.dismantle_materials || []} onChange={v => u("dismantle_materials", v)} items={allItems} />
      {/* Statistiche personalizzate: quando attive, backend accetta i valori
          verbatim e il combat engine può leggerli per calcolare potere,
          tetto danno e durata dell'oggetto. */}
      <div className="panel-inner p-3 border border-[#333] space-y-2" data-testid="item-custom-stats">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" data-testid="item-custom-stats-toggle"
            checked={!!it.custom_stats}
            onChange={e => u("custom_stats", e.target.checked)}
            className="accent-[#E55A00]" />
          <span className="font-heading text-[10px] tracking-[0.25em] uppercase text-[#E55A00]">Statistiche personalizzate</span>
          <span className="font-mono text-[9px] text-[#666]">(potere · tetto danno · durata a scelta)</span>
        </label>
        {it.custom_stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <Input label="Potere" type="number" value={it.power ?? 1}
              onChange={v => u("power", Number(v) || 0)} testid="item-power" />
            <Input label="Tetto danno" type="number" value={it.damage_cap ?? 0}
              onChange={v => u("damage_cap", Number(v) || 0)} testid="item-damage-cap" />
            <Input label="Durata (turni)" type="number" value={it.duration ?? 0}
              onChange={v => u("duration", Number(v) || 0)} testid="item-duration" />
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Input label="Valore (oro)" type="number" value={it.gold_value} onChange={v => u("gold_value", v)} testid="item-gold" />
        <Input label="Peso (kg)" type="number" value={it.weight} onChange={v => u("weight", Number(v))} testid="item-weight" />
        <label className="block"><div className="font-mono text-[9px] tracking-widest uppercase text-[#8c8c8c] mb-1">Stackabile</div>
          <select data-testid="item-stack" value={it.stackable ? "1" : "0"} onChange={e => u("stackable", e.target.value === "1")}
            className="w-full bg-[#0a0a0a] border border-[#333] px-2 py-1.5 text-xs">
            <option value="1">Sì</option><option value="0">No</option>
          </select>
        </label>
        <Input label="Quantità max" type="number" value={it.max_stack} onChange={v => u("max_stack", v)} testid="item-max-stack" />
      </div>
      <ImageField value={it.image} onChange={v => u("image", v)} testid="item-image" />
      <AnimationPicker value={it.animations} onChange={v => u("animations", v)} testid="item-anims" />
      <Input label="Note" textarea rows={2} value={it.notes} onChange={v => u("notes", v)} testid="item-notes" />
      <div className="flex justify-end gap-2 pt-2 border-t border-[#262626]">
        <button onClick={onCancel} className="px-3 py-2 border border-[#262626] font-heading text-[10px] tracking-widest uppercase text-[#8c8c8c]">Annulla</button>
        <button data-testid="item-save" onClick={() => onSave(it)} className="px-3 py-2 border border-[#6E8B3D] text-[#6E8B3D] font-heading text-[10px] tracking-widest uppercase hover:bg-[#4A5D23]/10"><Save className="w-3 h-3 inline mr-1" /> Salva</button>
      </div>
    </Modal>
  );
}

function ItemView({ it, onClose, onEdit }) {
  const [showSim, setShowSim] = useState(false);
  return (
    <Modal title={it.name} onClose={onClose} testid="gm-item-view">
      <div className="flex gap-4">
        <GMImage src={it.image} animations={it.animations} size={140} testid="item-view-img" />
        <div className="flex-1 space-y-1">
          <div className="font-heading text-lg uppercase tracking-widest" style={{ color: getRarityColor(it.rarity) }}>{it.name}</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#B89C3A]">{it.type} · {it.rarity}</div>
          <div className="text-xs text-[#B0ADA5]">{it.description}</div>
          <div className="font-mono text-[10px] text-[#B0ADA5]">Valore: {it.gold_value}g · Peso: {it.weight}kg</div>
          <div className="font-mono text-[10px] text-[#B0ADA5]">Requisiti: {it.requirements}</div>
          <div className="font-mono text-[10px] text-[#B0ADA5]">{it.stackable ? `Stack max: ${it.max_stack}` : "Non stackabile"}</div>
          {it.custom_stats && (
            <div className="font-mono text-[10px] text-[#E55A00]">
              Potere {it.power ?? 1}
              {typeof it.damage_cap === "number" && it.damage_cap > 0 ? ` · Tetto ${it.damage_cap}` : ""}
              {typeof it.duration === "number" && it.duration > 0 ? ` · Durata ${it.duration}t` : ""}
            </div>
          )}
        </div>
      </div>
      {it.effects?.length > 0 && <Section title="Effetti"><Chips items={it.effects} color={getRarityColor(it.rarity)} /></Section>}
      {it.notes && <Section title="Note">{it.notes}</Section>}
      <div className="flex justify-end gap-2 pt-2 border-t border-[#262626]">
        <button data-testid="item-simulate-btn" onClick={() => setShowSim(true)}
          className="px-3 py-2 border border-[#E55A00] text-[#E55A00] font-heading text-[10px] tracking-widest uppercase hover:bg-[#E55A00]/10 flex items-center gap-1.5">
          <Zap className="w-3 h-3" /> Prova su un nemico
        </button>
        <button onClick={onEdit} className="px-3 py-2 border border-[#B89C3A] text-[#E5C34A] font-heading text-[10px] tracking-widest uppercase">Modifica</button>
      </div>
      {showSim && <ItemSimulatorModal item={it} onClose={() => setShowSim(false)} />}
    </Modal>
  );
}

function ItemSimulatorModal({ item, onClose }) {
  // Fetches the GM enemy roster once + lets the admin pick a target, dial in
  // an attacker level, and stream back a 20-turn simulation. Read-only; no DB
  // side-effects (see backend `/admin/gm/items/simulate`).
  const enemies = gmStore.enemies.list();
  const [enemyId, setEnemyId] = useState(enemies[0]?.id || "");
  const [attackerLevel, setAttackerLevel] = useState(item.level ? Math.max(1, item.level + 2) : 10);
  const [turns, setTurns] = useState(20);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);

  const run = async () => {
    const enemy = enemies.find(e => e.id === enemyId);
    if (!enemy) { toast.error("Seleziona un nemico"); return; }
    setRunning(true);
    setResult(null);
    try {
      const r = await api.gmSimulateItem({
        item, enemy,
        attacker_level: Number(attackerLevel) || 10,
        turns: Number(turns) || 20,
      });
      setResult(r);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Simulazione fallita");
    } finally {
      setRunning(false);
    }
  };

  return (
    <Modal title={`Simulatore: ${item.name || "Oggetto"}`} onClose={onClose} wide testid="item-simulator">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <div className="font-mono text-[9px] tracking-widest uppercase text-[#8c8c8c] mb-1">Nemico bersaglio</div>
          <select data-testid="sim-enemy-select" value={enemyId} onChange={e => setEnemyId(e.target.value)}
            className="w-full bg-[#0a0a0a] border border-[#333] px-2 py-1.5 text-xs text-[#E2DFD8]">
            {enemies.length === 0 && <option value="">— nessun nemico configurato —</option>}
            {enemies.map(e => (
              <option key={e.id} value={e.id}>
                Lv{e.level} · {e.name} · HP{e.hp} DEF{e.def}
              </option>
            ))}
          </select>
        </div>
        <Input label="Livello attaccante" type="number" value={attackerLevel} onChange={setAttackerLevel} testid="sim-attacker-level" />
        <Input label="Turni (max 50)" type="number" value={turns} onChange={setTurns} testid="sim-turns" />
      </div>
      <div className="flex justify-end gap-2 pt-2 border-t border-[#262626]">
        <button onClick={onClose} className="px-3 py-2 border border-[#262626] font-heading text-[10px] tracking-widest uppercase text-[#8c8c8c]">Chiudi</button>
        <button data-testid="sim-run-btn" onClick={run} disabled={running || !enemyId}
          className="px-3 py-2 border border-[#E55A00] text-[#E55A00] font-heading text-[10px] tracking-widest uppercase hover:bg-[#E55A00]/10 disabled:opacity-40 flex items-center gap-1.5">
          <Zap className="w-3 h-3" /> {running ? "Simulazione in corso..." : "Simula"}
        </button>
      </div>
      {result && (
        <div className="space-y-3 mt-3" data-testid="sim-result">
          {/* Summary card */}
          <div className="panel-inner p-3 border border-[#E55A00] grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
            <SimStat label="Verdict" value={result.verdict} tone="orange" testid="sim-verdict" />
            <SimStat label="Turni per uccidere" value={result.turns_to_kill ?? "—"} testid="sim-turns-to-kill" />
            <SimStat label="Danno medio / turno" value={result.avg_damage} testid="sim-avg" />
            <SimStat label="Danno totale" value={result.total_damage} testid="sim-total" />
            <SimStat label="Colpi critici" value={`${result.crits}/${result.hits_landed}`} />
            <SimStat label="HP nemico" value={result.enemy_max_hp} />
            <SimStat label="HP giocatore rimasti" value={`${result.player_hp_end}/${result.player_max_hp}`} />
            <SimStat label="Personalizzato" value={result.custom_stats_used ? `⚡ power ${result.power}` : "no"} />
          </div>
          {/* Per-turn log */}
          <div className="panel-inner border border-[#262626] max-h-64 overflow-auto">
            <table className="w-full text-[10px] font-mono">
              <thead className="sticky top-0 bg-[#0a0a0a] border-b border-[#262626]">
                <tr className="text-[#8c8c8c] tracking-widest uppercase">
                  <th className="p-1 text-left">Turno</th>
                  <th className="p-1 text-right">Dan.</th>
                  <th className="p-1 text-right">Fis.</th>
                  <th className="p-1 text-right">Elem.</th>
                  <th className="p-1 text-center">Crit</th>
                  <th className="p-1 text-right">HP Nemico</th>
                  <th className="p-1 text-right">Ritors.</th>
                  <th className="p-1 text-right">HP Player</th>
                </tr>
              </thead>
              <tbody>
                {result.turns.map((t, i) => (
                  <tr key={i} className={`border-b border-[#1a1a1a] ${t.crit ? "text-[#E5C34A]" : "text-[#B0ADA5]"}`}>
                    <td className="p-1">{t.turn}</td>
                    <td className="p-1 text-right font-bold">{t.damage}</td>
                    <td className="p-1 text-right">{t.phys}</td>
                    <td className="p-1 text-right">
                      {t.elem}
                      {t.el_note ? ` (${t.el_note})` : ""}
                    </td>
                    <td className="p-1 text-center">{t.crit ? "✦" : ""}</td>
                    <td className="p-1 text-right">{t.enemy_hp}</td>
                    <td className="p-1 text-right text-[#E33333]">{t.enemy_dmg}</td>
                    <td className="p-1 text-right">{t.player_hp}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Modal>
  );
}

function SimStat({ label, value, tone = "neutral", testid }) {
  const color = tone === "orange" ? "#E55A00" : "#E2DFD8";
  return (
    <div className="border border-[#262626] px-2 py-1.5">
      <div className="font-mono text-[8px] uppercase tracking-widest text-[#666]">{label}</div>
      <div className="font-heading text-[13px] tracking-widest" style={{ color }} data-testid={testid}>{value}</div>
    </div>
  );
}

function StatPresetPicker({ value, onChange, presets, testid, entity, onEntityChange }) {
  const selected = presets.find(p => p.key === value) || presets[0];
  const isCustom = value === "custom";
  const applyPreset = (p) => {
    onChange(p.key);
    // When the admin picks a real preset AND we have an entity handle, we
    // seed the raw stat fields so switching back to Custom later keeps the
    // last preset's values as the starting point.
    if (p.key !== "custom" && entity && onEntityChange) {
      const next = { ...entity, stat_preset: p.key };
      CUSTOM_STAT_KEYS.forEach(k => { if (p[k] !== undefined) next[k] = p[k]; });
      onEntityChange(next);
    }
  };
  return (
    <div className="panel-inner p-3 border border-[#333] space-y-2" data-testid={testid}>
      <div className="font-heading text-[10px] tracking-[0.25em] uppercase text-[#E5C34A]">Profilo statistiche</div>
      <select value={value || presets[0]?.key || ""} onChange={e => {
          const picked = presets.find(p => p.key === e.target.value) || presets[0];
          applyPreset(picked);
        }}
        className="w-full bg-[#0a0a0a] border border-[#333] px-2 py-1.5 text-xs text-[#E2DFD8]"
        data-testid={`${testid}-select`}>
        {presets.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
      </select>
      {!isCustom && selected && (
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-1">
          {["hp","attack","def","speed","str","dex","con","int","cha","xp","gold"].filter(k => selected[k] !== undefined).map(k => (
            <div key={k} className="border border-[#262626] px-1.5 py-1 text-center">
              <div className="font-mono text-[8px] uppercase text-[#666]">{k}</div>
              <div className="font-mono text-[10px] text-[#B0ADA5]">{selected[k]}</div>
            </div>
          ))}
        </div>
      )}
      {isCustom && entity && onEntityChange && (
        <div className="space-y-2">
          <div className="font-mono text-[9px] uppercase tracking-widest text-[#E55A00]">
            Modalità libera: ogni valore che digiti verrà salvato così com'è.
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
            {CUSTOM_STAT_KEYS.map(k => (
              <div key={k} className="flex flex-col">
                <label className="font-mono text-[8px] uppercase tracking-widest text-[#8c8c8c]" htmlFor={`${testid}-${k}`}>{k}</label>
                <input id={`${testid}-${k}`} data-testid={`${testid}-${k}`} type="number"
                  value={entity[k] ?? 0}
                  onChange={e => onEntityChange({ ...entity, [k]: Number(e.target.value) || 0 })}
                  className="bg-[#0a0a0a] border border-[#333] px-1.5 py-1 text-xs text-[#E2DFD8] font-mono focus:border-[#E55A00] outline-none" />
              </div>
            ))}
          </div>
        </div>
      )}
      {!isCustom && (
        <div className="font-mono text-[9px] text-[#666]">Le statistiche di combattimento sono fissate dal profilo selezionato. Passa a "Personalizzato" per libertà totale.</div>
      )}
    </div>
  );
}

// ── CLASSES SECTION ───────────────────────────────────────────────

function ClassesSection() {
  const [list, setList] = useState(gmStore.classes.list());
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const refresh = () => setList(gmStore.classes.list());
  const filtered = list.filter(c => !q || String(c.name || "").toLowerCase().includes(q.toLowerCase()));

  const save = async (c) => {
    const name = String(c.name || "").trim();
    if (!name) return toast.error("Inserisci il nome della classe");
    if (!c.description?.trim()) return toast.error("Inserisci la descrizione della classe");
    const stats = Object.fromEntries(GM_STATS.map(k => [k, Number(c.starting_stats?.[k] ?? 0)]));
    if (Object.values(stats).some(v => !Number.isFinite(v) || v < 0)) {
      return toast.error("Le statistiche iniziali devono essere numeri validi");
    }
    if (!c.primary_stat) return toast.error("Seleziona la statistica principale");
    // Normalize resource_pool → mage/martial for backend compatibility.
    const rp = (c.resource_pool || "vigor").toLowerCase();
    const derivedMage = rp === "mana" || rp === "hybrid";
    const derivedMartial = rp === "vigor" || rp === "hybrid";
    const skills = (c.skills || []).map((s, i) => {
      const base = {
        id: String(s.id || "").trim() || slugSkill(s.name, i),
        name: String(s.name || "").trim(),
        unlock: Math.max(1, Number(s.unlock || 1)),
        cost_pool: s.cost_pool === "mana" ? "mana" : "vigor",
        cost: Math.max(0, Number(s.cost || 0)),
        power: Math.max(0, Number(s.power || 0)),
        desc: String(s.desc || "").trim(),
      };
      if (s.applies_dot) {
        base.applies_dot = true;
        base.dot_damage = Math.max(0, Number(s.dot_damage || 0));
        base.dot_duration = Math.max(1, Number(s.dot_duration || 3));
      }
      if (s.deals_elemental && s.element) {
        base.deals_elemental = true;
        base.element = String(s.element);
        base.element_damage = Math.max(0, Number(s.element_damage || 0));
      }
      return base;
    }).filter(s => s.name);
    const clean = {
      ...c,
      id: c.id || crypto.randomUUID(),
      name,
      description: c.description.trim(),
      starting_stats: stats,
      max_hp: Math.max(1, Number(c.max_hp || 1)),
      skills,
      primary_stat: c.primary_stat,
      resource_pool: rp,
      mage: derivedMage,
      martial: derivedMartial,
      visible: c.visible !== false,
      image: c.image || "",
    };
    gmStore.classes.upsert(clean);
    refresh();
    setEditing(null);
    toast.success("Classe salvata e sincronizzata con game_data.py");
  };

  const del = () => {
    gmStore.classes.remove(deleting.id);
    refresh();
    setDeleting(null);
    toast.success("Eliminata");
  };

  return (
    <div className="space-y-3">
      <Header title="Compilatore classi" count={list.length} testid="gm-classes"
        hideRandom onNew={() => setEditing(defaultClass())} />
      <div className="panel p-3 text-[11px] text-[#8c8c8c] font-mono">
        Compila esclusivamente i dati della classe. Il salvataggio aggiorna il catalogo GM,
        le abilità di classe e le sezioni corrispondenti di <span className="text-[#E5C34A]">game_data.py</span>.
      </div>
      <SearchBar value={q} onChange={setQ} testid="gm-classes" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map(c => (
          <div key={c.id} data-testid={`gm-class-card-${c.id}`} onClick={() => setViewing(c)}
            className="panel p-3 flex gap-3 cursor-pointer hover:border-[#E5C34A]">
            <GMImage src={c.image} animations={c.animations} size={72} testid={`gm-class-img-${c.id}`} />
            <div className="flex-1 min-w-0">
              <div className="font-heading text-[13px] uppercase tracking-widest text-[#E5C34A] truncate">{c.name}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#B89C3A]">
                {c.primary_stat || "—"} · {c.mage ? "MAGE" : ""}{c.mage && c.martial ? " / " : ""}{c.martial ? "MARTIAL" : ""}
              </div>
              <div className="text-[11px] text-[#B0ADA5] mt-1 line-clamp-2">{c.description}</div>
              <div className="font-mono text-[10px] text-[#666] mt-1">HP {c.max_hp} · {c.skills?.length || 0} abilità</div>
              <div className="flex gap-2 mt-2">
                <button onClick={ev => { ev.stopPropagation(); setEditing(c); }} className="font-mono text-[9px] uppercase tracking-widest text-[#B89C3A]">Modifica</button>
                <button onClick={ev => { ev.stopPropagation(); setDeleting(c); }} className="font-mono text-[9px] uppercase tracking-widest text-[#E33333] ml-auto">Elimina</button>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="col-span-full text-center text-xs italic text-[#666] py-8">Nessuna classe.</div>}
      </div>
      {editing && <ClassEditor initial={editing} onCancel={() => setEditing(null)} onSave={save} />}
      {viewing && <ClassView c={viewing} onClose={() => setViewing(null)} onEdit={() => { setEditing(viewing); setViewing(null); }} />}
      {deleting && <ConfirmDelete name={deleting.name} onCancel={() => setDeleting(null)} onConfirm={del} />}
    </div>
  );
}

function defaultClass() {
  return {
    id: crypto.randomUUID(),
    name: "",
    description: "",
    starting_stats: { strength: 8, defense: 8, dexterity: 8, intelligence: 8, charisma: 8, concentration: 8 },
    max_hp: 30,
    skills: [],
    primary_stat: "",
    resource_pool: "vigor",  // "vigor" | "mana" | "hybrid"  (§1)
    mage: false,
    martial: true,
    image: "",
    animations: ["idle-breathe"],
    visible: true,  // §6D: visibilità nella selezione
  };
}

function slugSkill(name, index) {
  const slug = String(name || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  return slug || `skill_${index + 1}`;
}

function ClassEditor({ initial, onCancel, onSave }) {
  const [c, set] = useState(() => {
    // Derive resource_pool from legacy mage/martial if not explicitly set.
    let rp = initial.resource_pool;
    if (!rp) {
      if (initial.mage && initial.martial) rp = "hybrid";
      else if (initial.mage) rp = "mana";
      else rp = "vigor";
    }
    return {
      ...defaultClass(),
      ...initial,
      starting_stats: { ...defaultClass().starting_stats, ...(initial.starting_stats || {}) },
      skills: (initial.skills || []).map(s => ({ ...s })),
      resource_pool: rp,
      visible: initial.visible !== false,
    };
  });
  const u = (k, v) => set(prev => ({ ...prev, [k]: v }));
  const updateStat = (k, v) => set(prev => ({ ...prev, starting_stats: { ...prev.starting_stats, [k]: Number(v) } }));
  const updateSkill = (i, k, v) => set(prev => {
    const skills = [...(prev.skills || [])];
    skills[i] = { ...skills[i], [k]: v };
    return { ...prev, skills };
  });
  const addSkill = () => set(prev => ({
    ...prev,
    skills: [...(prev.skills || []), { id: "", name: "", unlock: 1, cost_pool: "vigor", cost: 0, power: 1, desc: "" }],
  }));
  const removeSkill = i => set(prev => ({ ...prev, skills: prev.skills.filter((_, j) => j !== i) }));

  return (
    <Modal title={initial.name ? "Modifica Classe" : "Nuova Classe"} onClose={onCancel} wide testid="gm-class-editor">
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input label="NOME CLASSE" value={c.name} onChange={v => u("name", v)} testid="class-name" />
          <Input label="HP INIZIALI MASSIMI DELLA CLASSE" type="number" value={c.max_hp} onChange={v => u("max_hp", Number(v))} testid="class-max-hp" />
        </div>

        <Input label="DESCRIZIONE DELLA CLASSE" textarea rows={3} value={c.description}
          onChange={v => u("description", v)} testid="class-desc" />

        <div className="panel-inner p-3 border border-[#333] space-y-2" data-testid="class-starting-stats">
          <div className="font-mono text-[9px] tracking-widest uppercase text-[#E5C34A]">STATISTICHE INIZIALI DELLA CLASSE</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {GM_STATS.map(stat => (
              <Input key={stat} label={stat.toUpperCase()} type="number" value={c.starting_stats?.[stat] ?? 0}
                onChange={v => updateStat(stat, v)} testid={`class-stat-${stat}`} />
            ))}
          </div>
        </div>

        <div className="panel-inner p-3 border border-[#333] space-y-3" data-testid="class-skills">
          <div className="flex items-center">
            <div>
              <div className="font-mono text-[9px] tracking-widest uppercase text-[#E5C34A]">ABILITÀ DELLA CLASSE</div>
              <div className="font-mono text-[8px] text-[#666]">Ogni riga diventa un elemento di CLASS_SKILLS.</div>
            </div>
            <button type="button" onClick={addSkill}
              className="ml-auto px-2 py-1 border border-[#B89C3A] text-[#E5C34A] font-mono text-[9px] uppercase tracking-widest">
              <Plus className="w-3 h-3 inline mr-1" /> +
            </button>
          </div>
          {(c.skills || []).map((s, i) => (
            <div key={i} className="border border-[#262626] p-2 space-y-2">
              <div className="flex items-center gap-2">
                <span className="font-mono text-[9px] text-[#666]">ABILITÀ {i + 1}</span>
                <button type="button" onClick={() => removeSkill(i)} className="ml-auto text-[#E33333]"><Trash2 className="w-3 h-3" /></button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <Input label="id" value={s.id} onChange={v => updateSkill(i, "id", v)} />
                <Input label="name" value={s.name} onChange={v => updateSkill(i, "name", v)} />
                <Input label="unlock" type="number" value={s.unlock} onChange={v => updateSkill(i, "unlock", Number(v))} />
                <Input label="cost_pool" options={["vigor", "mana"]} value={s.cost_pool} onChange={v => updateSkill(i, "cost_pool", v)} />
                <Input label="cost" type="number" value={s.cost} onChange={v => updateSkill(i, "cost", Number(v))} />
                <Input label="power" type="number" step="0.1" value={s.power} onChange={v => updateSkill(i, "power", Number(v))} />
              </div>
              <Input label="desc" textarea rows={2} value={s.desc} onChange={v => updateSkill(i, "desc", v)} />
              {/* DoT toggle (§6D) */}
              <div className="border border-[#262626] p-2 space-y-1 bg-[#0a0a0a]">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!s.applies_dot}
                    data-testid={`skill-${i}-dot`}
                    onChange={e => updateSkill(i, "applies_dot", e.target.checked)}
                    className="accent-[#8A0303]" />
                  <span className="font-mono text-[9px] uppercase text-[#E33333]">Applica DoT</span>
                </label>
                {s.applies_dot && (
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <Input label="Danno DoT/turno" type="number" value={s.dot_damage} onChange={v => updateSkill(i, "dot_damage", Number(v))} testid={`skill-${i}-dot-damage`} />
                    <Input label="Durata (turni)" type="number" value={s.dot_duration ?? 3} onChange={v => updateSkill(i, "dot_duration", Number(v))} testid={`skill-${i}-dot-duration`} />
                  </div>
                )}
              </div>
              {/* Elemental damage toggle (§6D) */}
              <div className="border border-[#262626] p-2 space-y-1 bg-[#0a0a0a]">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!s.deals_elemental}
                    data-testid={`skill-${i}-elemental`}
                    onChange={e => updateSkill(i, "deals_elemental", e.target.checked)}
                    className="accent-[#E5C34A]" />
                  <span className="font-mono text-[9px] uppercase text-[#E5C34A]">Infligge danno elementale</span>
                </label>
                {s.deals_elemental && (
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <ElementSelect label="Elemento" value={s.element || ""} onChange={v => updateSkill(i, "element", v)} testid={`skill-${i}-element`} />
                    <Input label="Danno elementale" type="number" value={s.element_damage || 0} onChange={v => updateSkill(i, "element_damage", Number(v))} testid={`skill-${i}-element-damage`} />
                  </div>
                )}
              </div>
            </div>
          ))}
          {(c.skills || []).length === 0 && <div className="font-mono text-[10px] text-[#666]">Nessuna abilità. Usa + per aggiungerne una.</div>}
        </div>

        <div className="panel-inner p-3 border border-[#333] space-y-2" data-testid="class-primary-stat">
          <div className="font-mono text-[9px] tracking-widest uppercase text-[#E5C34A]">ASSEGNAZIONE STATISTICA PRINCIPALE ALLA CLASSE</div>
          <select value={c.primary_stat} onChange={e => u("primary_stat", e.target.value)}
            className="w-full bg-[#0a0a0a] border border-[#333] px-2 py-2 text-xs text-[#E2DFD8]">
            <option value="">Seleziona...</option>
            {["strength", "defense", "dexterity", "intelligence", "charisma"].map(stat => <option key={stat} value={stat}>{stat}</option>)}
          </select>
        </div>

        <div className="panel-inner p-3 border border-[#333] space-y-2" data-testid="class-resource-categories">
          <div className="font-mono text-[9px] tracking-widest uppercase text-[#E5C34A]">CATEGORIA RISORSE</div>
          <div className="flex flex-wrap gap-2">
            {[
              { key: "vigor",  label: "Vigor (Martial)",     color: "#6E8B3D" },
              { key: "mana",   label: "Mana (Mage)",         color: "#5D2F5F" },
              { key: "hybrid", label: "Hybrid (Vigor+Mana)", color: "#B89C3A" },
            ].map(opt => (
              <button key={opt.key} type="button"
                data-testid={`class-resource-${opt.key}`}
                onClick={() => u("resource_pool", opt.key)}
                className={`px-3 py-1.5 border font-mono text-[10px] uppercase tracking-widest transition-colors ${
                  c.resource_pool === opt.key
                    ? "border-[color:var(--gm-c)] text-[color:var(--gm-c)] bg-[color:var(--gm-c)]/10"
                    : "border-[#262626] text-[#666] hover:text-[#B0ADA5]"
                }`}
                style={{ "--gm-c": opt.color }}>
                {opt.label}
              </button>
            ))}
          </div>
          <div className="font-mono text-[9px] text-[#666]">
            Le classi Hybrid possiedono entrambe le pool: possono lanciare abilità che costano Mana E Vigor.
          </div>
        </div>

        <div className="panel-inner p-3 border border-[#333]" data-testid="class-visibility">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={c.visible !== false} onChange={e => u("visible", e.target.checked)} className="accent-[#B89C3A]" />
            <span className="font-mono text-[10px] uppercase">Visibile nella selezione delle classi</span>
          </label>
        </div>

        <ImageField value={c.image} onChange={v => u("image", v)} testid="class-image" />

        <div className="flex justify-end gap-2 pt-2 border-t border-[#262626]">
          <button onClick={onCancel} className="px-3 py-2 border border-[#262626] text-[#8c8c8c] font-heading text-[10px] tracking-widest uppercase">Annulla</button>
          <button data-testid="class-save" onClick={() => onSave(c)} className="px-3 py-2 border border-[#6E8B3D] text-[#6E8B3D] font-heading text-[10px] tracking-widest uppercase hover:bg-[#4A5D23]/10">
            <Save className="w-3 h-3 inline mr-1" /> Salva in game_data.py
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ClassView({ c, onClose, onEdit }) {
  return (
    <Modal title={c.name} onClose={onClose} wide testid="gm-class-view">
      <div className="flex gap-4">
        <GMImage src={c.image} animations={c.animations} size={160} testid="class-view-img" />
        <div className="flex-1 space-y-2">
          <div className="font-heading text-lg uppercase tracking-widest text-[#E5C34A]">{c.name}</div>
          <div className="text-xs text-[#B0ADA5]">{c.description}</div>
          <div className="font-mono text-[10px]">HP {c.max_hp} · stat principale: {c.primary_stat || "—"}</div>
          <div className="font-mono text-[10px]">Mana: {c.mage ? "SI" : "NO"} · Vigor: {c.martial ? "SI" : "NO"}</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
            {GM_STATS.map(k => <Stat key={k} label={k.toUpperCase()} value={c.starting_stats?.[k] ?? 0} />)}
          </div>
          {c.skills?.length > 0 && <Section title="Abilità">
            <div className="space-y-1">{c.skills.map(s => <div key={s.id} className="font-mono text-[10px] text-[#B0ADA5]">
              <span className="text-[#E5C34A]">{s.name}</span> · Lv {s.unlock} · {s.cost_pool} {s.cost} · x{s.power} — {s.desc}
            </div>)}</div>
          </Section>}
          <div className="flex justify-end pt-2 border-t border-[#262626]">
            <button onClick={onEdit} className="px-3 py-2 border border-[#B89C3A] text-[#E5C34A] font-heading text-[10px] tracking-widest uppercase">Modifica</button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function CharactersSection() {
  const [players, setPlayers] = useState([]);
  const [classes, setClasses] = useState(gmStore.classes.list());
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.gmActivePlayers();
      setPlayers(r.players || []);
      setClasses(gmStore.classes.list());
    } catch (e) {
      toast.error("Impossibile caricare i giocatori attivi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, 15000);
    return () => clearInterval(timer);
  }, []);

  const assignClass = async (player, className) => {
    if (!className || className === player.class) return;
    const cls = classes.find(c => c.name === className);
    if (!cls) return toast.error("Classe non trovata");
    try {
      await api.gmAssignPlayerClass(player.user_id, className);
      toast.success(`${player.name}: classe assegnata a ${className}. Livello e progressi conservati.`);
      setAssigning(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assegnazione classe fallita");
    }
  };

  return (
    <div className="space-y-3">
      <Header title="Giocatori attivi" count={players.length} testid="gm-characters" hideRandom hideNew />
      <div className="panel p-3 text-[11px] text-[#8c8c8c] font-mono">
        Sono mostrati i giocatori con presenza attiva negli ultimi 60 secondi.
        Cambiare classe aggiorna solo la classe scelta: livello, XP, inventario, statistiche e progressi non vengono azzerati.
      </div>
      <div className="space-y-2">
        {loading && <div className="panel p-5 text-center font-mono text-xs text-[#666]">Caricamento...</div>}
        {!loading && players.map(player => (
          <div key={player.user_id} className="panel p-3 flex flex-col md:flex-row md:items-center gap-3" data-testid={`gm-active-player-${player.user_id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-heading text-sm uppercase tracking-widest text-[#E5C34A]">{player.name || "Senza nome"}</div>
              <div className="font-mono text-[10px] text-[#B89C3A]">
                Lv {player.level ?? "—"} · Classe attuale: {player.class || "—"}
              </div>
              <div className="font-mono text-[9px] text-[#666]">Ultima attività: {player.last_seen ? new Date(player.last_seen).toLocaleTimeString() : "—"}</div>
            </div>
            <div className="flex items-center gap-2">
              <select value={assigning?.user_id === player.user_id ? assigning.className : ""}
                onChange={e => {
                  const value = e.target.value;
                  setAssigning({ user_id: player.user_id, className: value });
                  if (value) assignClass(player, value);
                }}
                className="bg-[#0a0a0a] border border-[#333] px-2 py-2 text-xs text-[#E2DFD8]"
                data-testid={`gm-active-player-class-${player.user_id}`}>
                <option value="">Assegna classe...</option>
                {classes.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
          </div>
        ))}
        {!loading && players.length === 0 && (
          <div className="panel p-6 text-center font-mono text-xs text-[#666]">Nessun giocatore attivo in questo momento.</div>
        )}
      </div>
    </div>
  );
}

function defaultCharacter() { return { id: crypto.randomUUID(), name: "", class_id: null, class_name_snapshot: "", level: 1, xp: 0, str: 10, def: 5, dex: 10, con: 10, int: 10, cha: 10, hp: 10, mana: 5, inventory: [], equipment: {}, abilities: [], background: "", alignment: "Neutrale", image: "", notes: "", animations: ["idle-float"] }; }

function CharEditor({ initial, classes, onCancel, onSave }) {
  const [ch, set] = useState(initial);
  const u = (k, v) => set({ ...ch, [k]: v });
  const items = gmStore.items.list();

  // Auto-suggest stats when class + level changes
  const autoCalc = () => {
    const cls = classes.find(c => c.id === ch.class_id);
    if (!cls) { toast.error("Seleziona prima una classe"); return; }
    const lv = ch.level || 1;
    const boosted = { ...ch, hp: 8 + lv * 6, mana: 5 + lv * 3 };
    if (cls.primary_attrs?.includes("Forza")) boosted.str = 10 + lv;
    if (cls.primary_attrs?.includes("Destrezza")) boosted.dex = 10 + lv;
    if (cls.primary_attrs?.includes("Intelligenza")) boosted.int = 10 + lv;
    if (cls.primary_attrs?.includes("Costituzione")) boosted.con = 10 + lv;
    if (cls.primary_attrs?.includes("Difesa")) boosted.def = 10 + lv;
    if (cls.primary_attrs?.includes("Carisma")) boosted.cha = 10 + lv;
    set(boosted);
    toast.success("Stat rigenerate da classe + livello");
  };

  return (
    <Modal title={initial.name ? "Modifica Personaggio" : "Nuovo Personaggio"} onClose={onCancel} wide testid="gm-char-editor">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Input label="Nome" value={ch.name} onChange={v => u("name", v)} testid="char-name" />
        <Input label="Classe" options={classes.map(c => ({ value: c.id, label: c.name }))} value={ch.class_id} onChange={v => u("class_id", v)} testid="char-class" />
        <Input label="Allineamento" value={ch.alignment} onChange={v => u("alignment", v)} testid="char-align"
          options={["Legale Buono","Neutrale Buono","Caotico Buono","Legale Neutrale","Neutrale","Caotico Neutrale","Legale Malvagio","Neutrale Malvagio","Caotico Malvagio"]} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Input label="Livello" type="number" value={ch.level} onChange={v => u("level", v)} testid="char-level" />
        <Input label="XP" type="number" value={ch.xp} onChange={v => u("xp", v)} testid="char-xp" />
        <Input label="HP" type="number" value={ch.hp} onChange={v => u("hp", v)} testid="char-hp" />
        <Input label="Mana" type="number" value={ch.mana} onChange={v => u("mana", v)} testid="char-mana" />
      </div>
      <div className="flex gap-2 items-end">
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 flex-1">
          {["str","def","dex","int","con","cha"].map(k => <Input key={k} label={k.toUpperCase()} type="number" value={ch[k]} onChange={v => u(k, v)} testid={`char-${k}`} />)}
        </div>
        <button data-testid="char-autocalc" onClick={autoCalc} className="px-2 py-1.5 border border-[#5D2F5F] text-[#A97AB0] hover:bg-[#5D2F5F]/10 font-heading text-[10px] tracking-widest uppercase flex items-center gap-1 whitespace-nowrap">
          <Wand2 className="w-3 h-3" /> Auto-calc
        </button>
      </div>
      <InventoryPicker items={items} selected={ch.inventory} onChange={v => u("inventory", v)} />
      <CSVField label="Abilità conosciute" value={ch.abilities} onChange={v => u("abilities", v)} testid="char-abilities" />
      <Input label="Background / Storia" textarea rows={3} value={ch.background} onChange={v => u("background", v)} testid="char-bg" />
      <ImageField value={ch.image} onChange={v => u("image", v)} testid="char-image" />
      <AnimationPicker value={ch.animations} onChange={v => u("animations", v)} testid="char-anims" />
      <Input label="Note" textarea rows={2} value={ch.notes} onChange={v => u("notes", v)} testid="char-notes" />
      <div className="flex justify-end gap-2 pt-2 border-t border-[#262626]">
        <button onClick={onCancel} className="px-3 py-2 border border-[#262626] text-[#8c8c8c] font-heading text-[10px] tracking-widest uppercase">Annulla</button>
        <button data-testid="char-save" onClick={() => onSave(ch)} className="px-3 py-2 border border-[#6E8B3D] text-[#6E8B3D] font-heading text-[10px] tracking-widest uppercase"><Save className="w-3 h-3 inline mr-1" /> Salva</button>
      </div>
    </Modal>
  );
}

function InventoryPicker({ items, selected = [], onChange }) {
  return <div>
    <div className="font-mono text-[9px] tracking-widest uppercase text-[#8c8c8c] mb-1">Inventario</div>
    <div className="flex flex-wrap gap-1 max-h-40 overflow-y-auto border border-[#262626] p-2 bg-[#0a0a0a]">
      {items.length === 0 && <span className="text-xs italic text-[#666]">Nessun oggetto disponibile. Creane prima.</span>}
      {items.map(it => {
        const on = selected.includes(it.id);
        return <button key={it.id} type="button" onClick={() => onChange(on ? selected.filter(x => x !== it.id) : [...selected, it.id])}
          data-testid={`inv-${it.id}`}
          className={`px-2 py-1 border font-mono text-[10px] uppercase tracking-widest ${on ? "border-[#6E8B3D] bg-[#4A5D23]/10" : "border-[#262626] text-[#B0ADA5]"}`}
          style={on ? { color: getRarityColor(it.rarity) } : {}}>
          {it.name}
        </button>;
      })}
    </div>
  </div>;
}

function CharView({ ch, onClose, onEdit }) {
  const cls = gmStore.classes.list().find(c => c.id === ch.class_id);
  const items = gmStore.items.list();
  return (
    <Modal title={ch.name} onClose={onClose} wide testid="gm-char-view">
      <div className="flex flex-col md:flex-row gap-4">
        <GMImage src={ch.image} animations={ch.animations} size={220} testid="char-view-img" />
        <div className="flex-1 space-y-2">
          <div className="font-heading text-xl uppercase tracking-widest text-[#E5C34A]">{ch.name}</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#B89C3A]">Lv {ch.level} · {cls?.name || ch.class_name_snapshot || "—"} · {ch.alignment}</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat label="HP" value={ch.hp} /><Stat label="Mana" value={ch.mana} /><Stat label="XP" value={ch.xp} />
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-1">
            {["str","def","dex","int","con","cha"].map(k => <Stat key={k} label={k.toUpperCase()} value={ch[k]} />)}
          </div>
        </div>
      </div>
      {ch.background && <Section title="Background">{ch.background}</Section>}
      {ch.abilities?.length > 0 && <Section title="Abilità"><Chips items={ch.abilities} /></Section>}
      {ch.inventory?.length > 0 && <Section title="Inventario">
        <div className="flex flex-wrap gap-1">
          {ch.inventory.map(id => {
            const it = items.find(i => i.id === id);
            return <span key={id} className="px-2 py-0.5 border font-mono text-[10px] uppercase tracking-widest"
              style={it ? { borderColor: getRarityColor(it.rarity), color: getRarityColor(it.rarity) } : { borderColor: "#666", color: "#666" }}>
              {it?.name || "(?)"}
            </span>;
          })}
        </div>
      </Section>}
      {ch.notes && <Section title="Note">{ch.notes}</Section>}
      <div className="flex justify-end pt-2 border-t border-[#262626]"><button onClick={onEdit} className="px-3 py-2 border border-[#B89C3A] text-[#E5C34A] font-heading text-[10px] tracking-widest uppercase">Modifica</button></div>
    </Modal>
  );
}

// ── WORLD BOSSES SECTION ─────────────────────────────────────────

function WorldBossesSection() {
  const [list, setList] = useState(gmStore.world_bosses.list());
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const refresh = () => setList(gmStore.world_bosses.list());
  const filtered = list.filter(b => !q || b.name.toLowerCase().includes(q.toLowerCase()));
  const doRandom = () => { const b = generateRandomWorldBoss(); gmStore.world_bosses.upsert(b); refresh(); toast.success(`Generato: ${b.name}`); };
  const save = (b) => {
    let toSave = { ...b };
    if (b.stat_preset === "custom") {
      CUSTOM_STAT_KEYS.forEach(k => { toSave[k] = Number(toSave[k] ?? 0) || 0; });
      // Legacy engine fields the boss encounter reads.
      toSave.max_hp = toSave.hp;
      toSave.defense = toSave.def;
    } else {
      const preset = STAT_PRESETS.find(p => p.key === b.stat_preset) || STAT_PRESETS.find(p => p.key === "boss");
      toSave = { ...toSave, hp: preset.hp, max_hp: preset.hp, attack: preset.attack, defense: preset.def };
    }
    gmStore.world_bosses.upsert(toSave); refresh(); setEditing(null); toast.success("Salvato");
  };
  const del = () => { gmStore.world_bosses.remove(deleting.id); refresh(); setDeleting(null); toast.success("Eliminato"); };
  const toggleActive = (b) => {
    if (b.active_week) {
      gmStore.world_bosses.upsert({ ...b, active_week: false });
      toast.success(`${b.name} disattivato`);
    } else {
      gmStore.world_bosses.setActive(b.id);
      toast.success(`${b.name} ora ATTIVO questa settimana`);
    }
    refresh();
  };

  return (
    <div className="space-y-3">
      <Header title="World Boss" count={list.length} testid="gm-wb" hideRandom onNew={() => setEditing(defaultWB())} />
      <div className="panel p-2 flex items-center gap-2 text-xs">
        <Crown className="w-3.5 h-3.5 text-[#E5C34A]" strokeWidth={1.5} />
        <span className="font-mono text-[10px] tracking-widest uppercase text-[#8c8c8c]">Boss attivo questa settimana:</span>
        <span className="font-heading text-xs tracking-widest uppercase text-[#E55A00]" data-testid="wb-active-name">
          {list.find(b => b.active_week)?.name || "— nessuno —"}
        </span>
      </div>
      <SearchBar value={q} onChange={setQ} testid="gm-wb" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map(b => (
          <div key={b.id} data-testid={`gm-wb-card-${b.id}`} onClick={() => setViewing(b)}
            className={`panel p-3 flex gap-3 cursor-pointer transition-colors ${b.active_week ? "border-[#E55A00]" : "hover:border-[#E5C34A]"}`}>
            <GMImage src={b.image} animations={b.animations} size={84} testid={`gm-wb-img-${b.id}`} />
            <div className="flex-1 min-w-0">
              <div className="font-heading text-[13px] uppercase tracking-widest text-[#E5C34A] truncate">{b.name}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#B89C3A]">Lv {b.level} · {b.difficulty} · {b.element}</div>
              <div className="font-mono text-[10px] text-[#B0ADA5]">HP {b.hp} · ATK {b.attack}</div>
              <div className="flex gap-2 mt-2 items-center">
                <button onClick={(ev) => { ev.stopPropagation(); toggleActive(b); }} data-testid={`gm-wb-toggle-${b.id}`}
                  className={`inline-flex items-center gap-1 px-2 py-1 border font-mono text-[9px] uppercase tracking-widest
                    ${b.active_week
                      ? "border-[#E55A00] text-[#E55A00] bg-[#E55A00]/10"
                      : "border-[#262626] text-[#B0ADA5] hover:border-[#E5C34A] hover:text-[#E5C34A]"}`}>
                  <Check className={`w-3 h-3 ${b.active_week ? "opacity-100" : "opacity-30"}`} strokeWidth={2} />
                  {b.active_week ? "Attivo" : "Attiva"}
                </button>
                <button onClick={(ev) => { ev.stopPropagation(); setEditing(b); }} className="font-mono text-[9px] uppercase tracking-widest text-[#B89C3A] hover:text-[#E5C34A]">Modifica</button>
                <button onClick={(ev) => { ev.stopPropagation(); setDeleting(b); }} className="font-mono text-[9px] uppercase tracking-widest text-[#E33333] ml-auto">Elimina</button>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="col-span-full text-center text-xs italic text-[#666] py-8">Nessun world boss. Usa "Genera Casuale" o "Nuovo".</div>}
      </div>
      {editing && <WBEditor initial={editing} onCancel={() => setEditing(null)} onSave={save} />}
      {viewing && <WBView b={viewing} onClose={() => setViewing(null)} onEdit={() => { setEditing(viewing); setViewing(null); }} />}
      {deleting && <ConfirmDelete name={deleting.name} onCancel={() => setDeleting(null)} onConfirm={del} />}
    </div>
  );
}

function defaultWB() { return { id: crypto.randomUUID(), name: "", level: 30, stat_preset: "boss", element: "", resistances: [], weaknesses: [], drops: [], difficulty: "Boss", phases: [], mechanics: [], reward_gold: 5000, reward_xp: 12000, reward_loot: "1 Leggendario garantito", lore: "", active_week: false, scheduled_from: "", scheduled_to: "", image: "", notes: "", animations: ["idle-breathe", "idle-pulse"] }; }

function WBEditor({ initial, onCancel, onSave }) {
  const [b, set] = useState(initial);
  const u = (k, v) => set({ ...b, [k]: v });
  const items = gmStore.items.list();
  return (
    <Modal title={initial.name ? "Modifica World Boss" : "Nuovo World Boss"} onClose={onCancel} wide testid="gm-wb-editor">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input label="Nome" value={b.name} onChange={v => u("name", v)} testid="wb-name" />
        <div><ElementSelect label="Elemento di attacco" value={b.element} onChange={v => u("element", v)} testid="wb-element" /></div>
        <Input label="Livello" type="number" value={b.level} onChange={v => u("level", v)} testid="wb-level" />
        <Input label="Difficoltà" value={b.difficulty} onChange={v => u("difficulty", v)} testid="wb-difficulty"
          options={["Sfida", "Elite", "Boss", "Leggenda", "Mitico"]} />
      </div>
      <StatPresetPicker value={b.stat_preset} onChange={v => u("stat_preset", v)} presets={STAT_PRESETS} testid="wb-stat-preset" entity={b} onEntityChange={set} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <ElementListEditor label="Resistenze elementali" value={b.resistances} onChange={v => u("resistances", v)} testid="wb-resistances" />
        <ElementListEditor label="Debolezze elementali" value={b.weaknesses} onChange={v => u("weaknesses", v)} testid="wb-weaknesses" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Input label="Ricompensa oro" type="number" value={b.reward_gold} onChange={v => u("reward_gold", v)} testid="wb-rgold" />
        <Input label="Ricompensa XP" type="number" value={b.reward_xp} onChange={v => u("reward_xp", v)} testid="wb-rxp" />
        <Input label="Loot descrizione" value={b.reward_loot} onChange={v => u("reward_loot", v)} testid="wb-rloot" />
      </div>
      <DropsField drops={b.drops} onChange={v => u("drops", v)} items={items} />
      <CSVField label="Fasi (una per riga)" value={b.phases} onChange={v => u("phases", v)} testid="wb-phases" />
      <CSVField label="Meccaniche" value={b.mechanics} onChange={v => u("mechanics", v)} testid="wb-mechanics" />
      <Input label="Lore" textarea rows={3} value={b.lore} onChange={v => u("lore", v)} testid="wb-lore" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input label="Attivo da (data)" type="date" value={b.scheduled_from} onChange={v => u("scheduled_from", v)} testid="wb-from" />
        <Input label="Attivo fino a (data)" type="date" value={b.scheduled_to} onChange={v => u("scheduled_to", v)} testid="wb-to" />
      </div>
      <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="wb-active-checkbox">
        <input type="checkbox" checked={!!b.active_week} onChange={e => u("active_week", e.target.checked)}
          className="accent-[#E55A00]" />
        <span className="font-mono text-[10px] tracking-widest uppercase text-[#E55A00]">Attivo questa settimana</span>
        <span className="font-mono text-[9px] text-[#666]">(disattiva automaticamente gli altri)</span>
      </label>
      <ImageField value={b.image} onChange={v => u("image", v)} testid="wb-image" />
      <AnimationPicker value={b.animations} onChange={v => u("animations", v)} testid="wb-anims" />
      <Input label="Note" textarea rows={2} value={b.notes} onChange={v => u("notes", v)} testid="wb-notes" />
      <div className="flex justify-end gap-2 pt-2 border-t border-[#262626]">
        <button onClick={onCancel} className="px-3 py-2 border border-[#262626] text-[#8c8c8c] font-heading text-[10px] tracking-widest uppercase">Annulla</button>
        <button data-testid="wb-save" onClick={() => onSave(b)} className="px-3 py-2 border border-[#6E8B3D] text-[#6E8B3D] font-heading text-[10px] tracking-widest uppercase hover:bg-[#4A5D23]/10"><Save className="w-3 h-3 inline mr-1" /> Salva</button>
      </div>
    </Modal>
  );
}

function WBView({ b, onClose, onEdit }) {
  return (
    <Modal title={b.name} onClose={onClose} wide testid="gm-wb-view">
      <div className="flex flex-col md:flex-row gap-4">
        <GMImage src={b.image} animations={b.animations} size={220} testid="wb-view-img" />
        <div className="flex-1 space-y-2">
          <div className="font-heading text-xl uppercase tracking-widest text-[#E5C34A]">{b.name}</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#B89C3A]">Lv {b.level} · {b.difficulty} · {b.element}</div>
          {b.active_week && <div className="inline-block px-2 py-0.5 border border-[#E55A00] text-[#E55A00] font-heading text-[10px] tracking-widest uppercase">Attivo questa settimana</div>}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat label="HP" value={`${b.hp}/${b.max_hp}`} /><Stat label="ATK" value={b.attack} /><Stat label="DEF" value={b.defense} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Stat label="Oro" value={b.reward_gold} /><Stat label="XP" value={b.reward_xp} /><Stat label="Loot" value={b.reward_loot} />
          </div>
        </div>
      </div>
      {b.lore && <Section title="Lore">{b.lore}</Section>}
      {b.phases?.length > 0 && <Section title="Fasi">
        <div className="space-y-1">{b.phases.map((p, i) => <div key={i} className="font-mono text-xs text-[#B0ADA5]"><span className="text-[#E5C34A]">{i+1}.</span> {p}</div>)}</div>
      </Section>}
      {b.mechanics?.length > 0 && <Section title="Meccaniche"><Chips items={b.mechanics} color="#E55A00" /></Section>}
      {(b.scheduled_from || b.scheduled_to) && <Section title="Programmazione">
        <div className="font-mono text-xs text-[#B0ADA5]">{b.scheduled_from || "—"} → {b.scheduled_to || "—"}</div>
      </Section>}
      {b.notes && <Section title="Note">{b.notes}</Section>}
      <div className="flex justify-end pt-2 border-t border-[#262626]"><button onClick={onEdit} className="px-3 py-2 border border-[#B89C3A] text-[#E5C34A] font-heading text-[10px] tracking-widest uppercase">Modifica</button></div>
    </Modal>
  );
}


// ── ABILITIES SECTION ─────────────────────────────────────────────

function AbilitiesSection() {
  const [list, setList] = useState(gmStore.abilities.list());
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const refresh = () => setList(gmStore.abilities.list());
  const filtered = useMemo(() => list.filter(a =>
    !q || (a.name || "").toLowerCase().includes(q.toLowerCase())
  ), [list, q]);

  const doRandom = () => { const a = generateRandomAbility(); gmStore.abilities.upsert(a); refresh(); toast.success(`Generata: ${a.name}`); };
  const save = (a) => { gmStore.abilities.upsert(a); refresh(); setEditing(null); toast.success("Salvato"); };
  const del = () => { gmStore.abilities.remove(deleting.id); refresh(); setDeleting(null); toast.success("Eliminato"); };

  return (
    <div className="space-y-3">
      <Header title="Abilità" count={list.length} testid="gm-abilities" hideRandom
        onNew={() => setEditing(defaultAbility())} />
      <SearchBar value={q} onChange={setQ} testid="gm-abilities" />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map(a => (
          <div key={a.id} data-testid={`gm-ability-card-${a.id}`}
            className="panel p-3 flex flex-col gap-2 cursor-pointer hover:border-[#E5C34A] transition-colors"
            onClick={() => setEditing(a)}>
            <div className="flex items-center justify-between gap-2">
              <div className="font-heading text-[13px] tracking-widest uppercase text-[#E5C34A] truncate">{a.name}</div>
              {a.source === "class_compiler" && <span className="font-mono text-[8px] uppercase tracking-widest text-[#666] border border-[#333] px-1.5 py-0.5">classe</span>}
            </div>
            <div className="font-mono text-[10px] text-[#B0ADA5]">Cost {a.cost} {a.cost_pool} · Pw {a.power} · unlock Lv {a.unlock}</div>
            <div className="font-mono text-[9px] text-[#8c8c8c]">Target: {a.target_type}</div>
            {a.description && <div className="font-body text-[11px] text-[#B0ADA5] italic line-clamp-2">{a.description}</div>}
            <div className="flex gap-2 mt-1">
              <button onClick={(ev) => { ev.stopPropagation(); setEditing(a); }} data-testid={`gm-ability-edit-${a.id}`}
                className="font-mono text-[9px] uppercase tracking-widest text-[#B89C3A] hover:text-[#E5C34A]">Modifica</button>
              {a.source !== "class_compiler" && <button onClick={(ev) => { ev.stopPropagation(); setDeleting(a); }} data-testid={`gm-ability-del-${a.id}`}
                className="font-mono text-[9px] uppercase tracking-widest text-[#E33333] hover:text-[#8A0303] ml-auto">Elimina</button>}
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="col-span-full text-center text-xs italic text-[#666] py-8">Nessuna abilità. Usa "Genera Casuale" o "Nuovo".</div>}
      </div>

      {editing && <AbilityEditor initial={editing} onCancel={() => setEditing(null)} onSave={save} />}
      {deleting && <ConfirmDelete name={deleting.name} onCancel={() => setDeleting(null)} onConfirm={del} />}
    </div>
  );
}

function defaultAbility() {
  return {
    id: crypto.randomUUID(), name: "", description: "",
    cost_pool: "mana", cost: 6, power: 1.5, unlock: 1,
    target_type: "both", class_ids: [], enemy_ids: [], image: "", notes: "",
  };
}

function AbilityEditor({ initial, onCancel, onSave }) {
  const [a, set] = useState(initial);
  const u = (k, v) => set({ ...a, [k]: v });
  return (
    <Modal title={initial.name ? "Modifica Abilità" : "Nuova Abilità"} onClose={onCancel} wide testid="gm-ability-editor">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input label="Nome" value={a.name} onChange={v => u("name", v)} testid="ability-name" />
        <Input label="Target" value={a.target_type} onChange={v => u("target_type", v)} options={["class", "enemy", "both"]} testid="ability-target" />
        <Input label="Pool costo" value={a.cost_pool} onChange={v => u("cost_pool", v)} options={["mana", "vigor"]} testid="ability-pool" />
        <Input label="Costo" type="number" value={a.cost} onChange={v => u("cost", v)} testid="ability-cost" />
        <Input label="Potenza (moltiplicatore)" type="number" value={a.power} onChange={v => u("power", v)} testid="ability-power" />
        <Input label="Sblocco al livello" type="number" value={a.unlock} onChange={v => u("unlock", v)} testid="ability-unlock" />
      </div>
      <Input label="Descrizione" textarea rows={2} value={a.description} onChange={v => u("description", v)} testid="ability-desc" />
      <ImageField value={a.image} onChange={v => u("image", v)} testid="ability-image" />
      <Input label="Note" textarea rows={2} value={a.notes} onChange={v => u("notes", v)} testid="ability-notes" />
      <div className="flex justify-end gap-2 pt-2 border-t border-[#262626]">
        <button onClick={onCancel} className="px-3 py-2 border border-[#262626] font-heading text-[10px] tracking-widest uppercase text-[#8c8c8c]">Annulla</button>
        <button data-testid="ability-save" onClick={() => onSave(a)}
          className="px-3 py-2 border border-[#6E8B3D] text-[#6E8B3D] font-heading text-[10px] tracking-widest uppercase hover:bg-[#4A5D23]/10 flex items-center gap-1.5">
          <Save className="w-3 h-3" /> Salva
        </button>
      </div>
    </Modal>
  );
}

/** Multi-select field for assigning abilities to a class or enemy. */
function AbilityAssignmentField({ value = [], onChange, target, testid }) {
  const abilities = gmStore.abilities.list().filter(a =>
    a.target_type === "both" || a.target_type === target
  );
  const selected = new Set(value || []);
  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(Array.from(next));
  };
  return (
    <div data-testid={testid}>
      <div className="font-mono text-[9px] tracking-widest uppercase text-[#8c8c8c] mb-1">Abilità assegnate</div>
      {abilities.length === 0 ? (
        <div className="font-mono text-[10px] italic text-[#666] border border-dashed border-[#262626] px-2 py-3 text-center">
          Nessuna abilità disponibile. Creane una nella tab "Abilità".
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {abilities.map(a => {
            const on = selected.has(a.id);
            return (
              <button key={a.id} type="button" onClick={() => toggle(a.id)}
                data-testid={`${testid}-toggle-${a.id}`}
                className={`px-2 py-1 border font-mono text-[10px] uppercase tracking-widest transition-colors
                  ${on ? "border-[#E5C34A] text-[#E5C34A] bg-[#B89C3A]/10" : "border-[#333] text-[#8c8c8c] hover:text-[#B0ADA5]"}`}>
                {on ? "✓ " : ""}{a.name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
